import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Helmet } from "react-helmet-async";
import { getLaunchText, trackEvent, trackEcommerceEvent } from "./utils";
import { Pdp } from "./components/Pdp";
import { LightningBolts } from "./components/LightningBolts";
import { RainEffect } from "./components/RainEffect";
import { OrderModal } from "./components/OrderModal";
import { PackingTableSection } from "./components/PackingTable";

const AdminPanel = React.lazy(() => import("./components/AdminPanel").then(module => ({ default: module.AdminPanel })));
const LegalPage = React.lazy(() => import("./components/LegalPage").then(module => ({ default: module.LegalPage })));
const DesktopSequence = React.lazy(() => import("./components/DesktopSequence").then(module => ({ default: module.DesktopSequence })));

declare global {
  interface Window {
    recaptchaVerifier: any;
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

const isImageUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  const lower = url.toLowerCase();
  
  if (
    lower.includes("lh3.googleusercontent.com") || 
    lower.includes("lh4.googleusercontent.com") || 
    lower.includes("lh5.googleusercontent.com") || 
    lower.includes("lh6.googleusercontent.com") ||
    lower.includes("googleusercontent.com/d/") ||
    lower.includes("drive.usercontent.google.com")
  ) {
    return true;
  }
  
  const videoExtensions = [".mp4", ".mov", ".webm", ".m4v", ".ogv", ".3gp", ".avi", ".mkv"];
  const urlPath = url.split("?")[0].toLowerCase();
  if (videoExtensions.some(ext => urlPath.endsWith(ext))) {
    return false;
  }
  
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp", ".heic"];
  if (imageExtensions.some(ext => urlPath.endsWith(ext))) {
    return true;
  }

  if (lower.includes("thumbnail") || lower.includes("image") || lower.includes("img") || lower.includes("pic")) {
    return true;
  }
  
  return false;
};

const PdpSkeleton = () => (
  <div className="bg-white text-[#1A1A1A] w-full mt-[64px] md:mt-[72px] min-h-screen">
    <section className="flex flex-col lg:flex-row items-start relative w-full bg-white mx-auto min-h-[calc(100dvh-64px)] md:min-h-[calc(100dvh-72px)]">
      {/* Left Column (Image Skeleton) */}
      <div className="w-full lg:w-[45%] sticky top-[64px] md:top-[72px] z-0 h-[65vh] md:h-[calc(100dvh-72px)] flex-shrink-0 bg-neutral-100 animate-pulse lg:border-r border-[#E5E5E5]/50"></div>

      {/* Right Column (Details Skeleton) */}
      <div className="flex-shrink-0 w-full lg:w-[55%] max-lg:relative max-lg:z-10 bg-white rounded-t-3xl lg:rounded-none max-lg:-mt-8 pt-6 pb-20 px-4 md:px-0">
        <div className="w-full sm:w-[90%] lg:w-[80%] xl:w-[70%] mx-auto mt-0 lg:mt-6">
          {/* Tags */}
          <div className="flex gap-2 mb-4">
            <div className="h-6 w-20 bg-neutral-100 rounded-full animate-pulse"></div>
            <div className="h-6 w-16 bg-neutral-100 rounded-full animate-pulse"></div>
          </div>

          {/* Title */}
          <div className="h-8 md:h-10 bg-neutral-100 rounded w-3/4 mb-2 animate-pulse mt-2"></div>
          <div className="h-4 bg-neutral-100 rounded w-1/3 mb-6 animate-pulse"></div>

          {/* Description */}
          <div className="space-y-2 mb-8">
            <div className="h-4 bg-neutral-100 rounded w-full animate-pulse"></div>
            <div className="h-4 bg-neutral-100 rounded w-full animate-pulse"></div>
            <div className="h-4 bg-neutral-100 rounded w-5/6 animate-pulse"></div>
          </div>

          {/* Price & CTA */}
          <div className="h-6 bg-neutral-100 rounded w-1/4 mb-6 animate-pulse"></div>
          <div className="h-[120px] bg-neutral-100 rounded-2xl w-full mb-6 animate-pulse"></div>
          <div className="h-12 bg-neutral-100 rounded-full w-full md:w-1/2 animate-pulse mt-10"></div>
        </div>
      </div>
    </section>
  </div>
);

const DEFAULT_PRODUCTS: any[] = [
  {
    id: 1,
    name: "Sands of Oud",
    notes: "Oud, Honey, Vanilla",
    price: "₹3,000",
    image:
      "https://lh3.googleusercontent.com/d/1F-EX07cgOg_MoDvQTjT9zpBH1GJykcoQ",
    inventory: 0,
    capacity: "50 ML",
    dropDate: "",
    soldOutTime: "5",
    description:
      "Experience the mesmerizing allure of Sands of Oud, a masterfully blended fragrance that captures the essence of desert twilight. With top notes of warm honey descending into a deep, resonant base of aged oud and sweet vanilla, this scent offers an unforgettable journey of the senses. Handcrafted with the finest ingredients from around the world.",
  },
  {
    id: 2,
    name: "Opal Glow",
    notes: "Floral, Fruity",
    price: "₹2,500",
    image:
      "https://lh3.googleusercontent.com/d/1wMa2tP5byjQB1coF45Sq3BAIIp_oTw_j",
    inventory: 0,
    dropDate: "20th June 2026",
    soldOutTime: "",
    description:
      "A radiant burst of blooming florals paired with vibrant fruit notes. Opal Glow embodies the freshness of early morning dew on a spring day.",
  },
  {
    id: 3,
    name: "Golden Tropic",
    notes: "Fresh, Fruity, Woody",
    price: "₹2,500",
    image:
      "https://lh3.googleusercontent.com/d/1l0LUSStG7goqKO4maV0oLmzdZBtJr6tJ",
    inventory: 0,
    dropDate: "20th June 2026",
    description:
      "Transport yourself to a sun-drenched island with Golden Tropic. Brilliantly fresh, juicy fruit notes smoothly transition to an elegant woody base.",
  },
  {
    id: 4,
    name: "Summer Veil",
    notes: "Aromatic, Musk",
    price: "₹2,500",
    image:
      "https://lh3.googleusercontent.com/d/1j5eYQcqWzXS1_CPK-PxnLn2EgJhfZRqJ",
    inventory: 0,
    dropDate: "20th June 2026",
    description:
      "A light, effervescent fragrance with a captivating aromatic opening that settles into a soft, comforting musk trail. Perfect for summer days.",
  },
  {
    id: 5,
    name: "Bloom Whisper",
    notes: "Floral, Fruity",
    price: "₹2,500",
    image:
      "https://lh3.googleusercontent.com/d/1bdvsBH-8nBAxPcgRp-SZCyHkyJbKFPIr",
    inventory: 0,
    dropDate: "20th June 2026",
    description:
      "Delicate and intimate, Bloom Whisper wraps you in a soft floral bouquet punctuated by subtle, sparkling fruit accords.",
  },
  {
    id: 6,
    name: "Victory Gold",
    notes: "Oud, Honey, Vanilla",
    price: "₹2,500",
    image:
      "https://lh3.googleusercontent.com/d/1tjy-O4nCymNBs9YIGJRhESWVNpvsk2m3",
    inventory: 20, // Default to live
    capacity: "50 ML",
    dropDate: "",
    soldOutTime: "15",
    description:
      "A triumphant and bold scent profile. Honeyed sweetness combined with robust oud and warm vanilla creates a powerfully luxurious aura.",
  },
  {
    id: 7,
    name: "Wild Horizon",
    notes: "Oud, Honey, Vanilla",
    price: "₹2,500",
    image:
      "https://lh3.googleusercontent.com/d/1GChteY3490R-e0La66iJ7CQraeqQuOjC",
    inventory: 5, // Default to live
    capacity: "50 ML",
    dropDate: "",
    soldOutTime: "20",
    description:
      "An adventurous interpretation of classic notes. Let Wild Horizon expand your senses with its complex, evolving layers of honeyed oud and vanilla.",
  },
];

import { ProductCardInfo } from "./components/ProductCardInfo";

// Products are now fetched from Google Sheets via the /api/products route
export default function App() {
  const [products, setProducts] = useState<any[]>(DEFAULT_PRODUCTS); // Optimized for instant load
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  const [carouselItems, setCarouselItems] = useState<any[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoadingCarousel, setIsLoadingCarousel] = useState(true);

  const [mobileHeroVideoUrl, setMobileHeroVideoUrl] = useState<string | null>(null);
  const [desktopHeroVideoUrl, setDesktopHeroVideoUrl] = useState<string | null>(null);
  const [mobileVideoError, setMobileVideoError] = useState<{ code: number; message: string } | null>(null);
  const [desktopVideoError, setDesktopVideoError] = useState<{ code: number; message: string } | null>(null);
  const [mobileVideoReady, setMobileVideoReady] = useState(false);
  const [desktopVideoReady, setDesktopVideoReady] = useState(false);

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const utmParams: Record<string, string> = {};
      let hasUtm = false;
      searchParams.forEach((value, key) => {
        if (key.startsWith("utm_") || key === "fbclid") {
          utmParams[key] = value;
          hasUtm = true;
        }
      });
      if (hasUtm) {
        sessionStorage.setItem("utmParams", JSON.stringify(utmParams));
      }
    } catch (e) {
      console.warn("Failed to capture UTM params", e);
    }
  }, []);

  const isDevelopment = useMemo(() => {
    return import.meta.env.DEV || 
           window.location.hostname === "localhost" || 
           window.location.hostname.includes("127.0.0.1") ||
           window.location.hostname.includes("ais-dev");
  }, []);

  const mobileCarouselItem = useMemo(() => {
    return carouselItems.find(item => item.applicability === "mobile" || item.applicability === "both") || carouselItems[0] || {};
  }, [carouselItems]);

  const desktopCarouselItem = useMemo(() => {
    return carouselItems.find(item => item.applicability === "web" || item.applicability === "both") || carouselItems[0] || {};
  }, [carouselItems]);

  const isMobileMediaImage = useMemo(() => {
    const mobileTarget = mobileCarouselItem?.imageUrl || mobileCarouselItem?.image || mobileCarouselItem?.media;
    return isImageUrl(mobileTarget);
  }, [mobileCarouselItem]);

  const isDesktopMediaImage = useMemo(() => {
    const desktopTarget = desktopCarouselItem?.imageUrl || desktopCarouselItem?.image || desktopCarouselItem?.media;
    return isImageUrl(desktopTarget);
  }, [desktopCarouselItem]);

  useEffect(() => {
    if (!carouselItems || carouselItems.length === 0) return;

    // Reset loading and error states on media link source changes
    setMobileVideoReady(false);
    setDesktopVideoReady(false);
    setMobileVideoError(null);
    setDesktopVideoError(null);

    const mobileTarget = mobileCarouselItem?.imageUrl || mobileCarouselItem?.image || mobileCarouselItem?.media;
    const desktopTarget = desktopCarouselItem?.imageUrl || desktopCarouselItem?.image || desktopCarouselItem?.media;

    if (mobileTarget) {
      if (mobileTarget.includes("drive.google.com")) {
        setMobileHeroVideoUrl(`/api/video-proxy?url=${encodeURIComponent(mobileTarget)}`);
      } else {
        setMobileHeroVideoUrl(mobileTarget);
      }
    } else {
      setMobileVideoReady(true);
    }

    if (desktopTarget) {
      if (desktopTarget.includes("drive.google.com")) {
        setDesktopHeroVideoUrl(`/api/video-proxy?url=${encodeURIComponent(desktopTarget)}`);
      } else {
        setDesktopHeroVideoUrl(desktopTarget);
      }
    } else {
      setDesktopVideoReady(true);
    }
  }, [carouselItems, mobileCarouselItem, desktopCarouselItem]);

  const [collectionsItems, setCollectionsItems] = useState<any[]>([]);
  const [isLoadingCollections, setIsLoadingCollections] = useState(true);
  const [ribbonItems, setRibbonItems] = useState<{text: string}[]>([
    { text: "Summer Collection - 20th June 2026" },
    { text: "Waitlist Open Now" }
  ]);
  const collectionsScrollRef = useRef<HTMLDivElement>(null);
  const firstSetRef = useRef<HTMLDivElement>(null);
  const secondSetRef = useRef<HTMLDivElement>(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeftState, setScrollLeftState] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!collectionsScrollRef.current) return;
    setIsAutoScrolling(false);
    setIsDragging(true);
    setStartX(e.pageX - collectionsScrollRef.current.offsetLeft);
    setScrollLeftState(collectionsScrollRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    setTimeout(() => setIsAutoScrolling(true), 1500);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !collectionsScrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - collectionsScrollRef.current.offsetLeft;
    const walk = (x - startX) * 2; // scroll-fast
    collectionsScrollRef.current.scrollLeft = scrollLeftState - walk;
  };

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeModal, setActiveModal] = useState<any>(null);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [isWaitlistSuccess, setIsWaitlistSuccess] = useState(false);
  const [waitlistName, setWaitlistName] = useState("");
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistPhone, setWaitlistPhone] = useState("");
  const [waitlistError, setWaitlistError] = useState("");
  const [isSubmittingWaitlist, setIsSubmittingWaitlist] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const [viewingPdpProduct, setViewingPdpProduct] = useState<any>(() => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path !== "/" && path.length > 1 && path !== "/admin_access" && path !== "/terms" && path !== "/privacy" && path !== "/refund") {
        const urlName = decodeURIComponent(path.substring(1)).toLowerCase().replace(/[^a-z0-9]/g, '');
        const found = DEFAULT_PRODUCTS.find(item => item?.name?.toLowerCase().replace(/[^a-z0-9]/g, '') === urlName) || null;
        if (found && found.accessMode === "waitlist" && !found.showPdp) return null;
        return found;
      }
    }
    return null;
  });
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);

  const navigateToPdp = (product: any) => {
    if (product) {
      if (product.accessMode === "waitlist" && !product.showPdp) {
        setSelectedProductId(product.id);
        setIsWaitlistOpen(true);
        return;
      }
      trackEcommerceEvent("view_item", product);
      const urlName = product.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase().replace(/-+/g, '-').replace(/^-|-$/g, '');
      const newPath = `/${urlName}`;
      window.history.pushState(
        null,
        "",
        newPath,
      );
      setCurrentPath(newPath);
    } else {
      window.history.pushState(null, "", "/");
      setCurrentPath("/");
    }
    setViewingPdpProduct(product);
  };

  useEffect(() => {
    const handlePopState = () => setCurrentPath(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Track PageView on route change for both GA4 and Meta Pixel
  useEffect(() => {
    if (typeof window !== "undefined") {
      const pageTitle = viewingPdpProduct 
        ? `${viewingPdpProduct.name} | Kayoki` 
        : "Kayoki | Luxury Fragrances";

      // Queue GA4 page_view event
      trackEvent("page_view", {
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: window.location.pathname
      });

      // Track Meta Pixel PageView
      if ((window as any).fbq) {
        (window as any).fbq('track', 'PageView');
      }
    }
  }, [currentPath, viewingPdpProduct]);

  useEffect(() => {
    const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (!gaId || currentPath === "/admin_access") return;

    const pageTitle = viewingPdpProduct 
      ? `${viewingPdpProduct.name} | Kayoki` 
      : "Kayoki | Luxury Fragrances";

    const configParams = {
      page_title: pageTitle,
      page_path: currentPath,
      page_location: window.location.href,
      send_page_view: false,
      custom_map: {
        'dimension1': 'step',
        'dimension2': 'field_name',
        'dimension3': 'note_type',
        'dimension4': 'value',
        'dimension5': 'transaction_id'
      }
    };

    if (window.gtag) {
      window.gtag("config", gaId, configParams);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(["config", gaId, configParams]);
    }
  }, [currentPath, viewingPdpProduct]);

  useEffect(() => {
    if (isLoadingProducts) return;

    const checkUrlForPdp = () => {
      const path = window.location.pathname;
      const hash = window.location.hash;
      
      if (path === "/admin_access") {
        return; // Handled below
      }
      
      if (path !== "/" && path.length > 1) {
        const urlName = decodeURIComponent(
          path.substring(1),
        ).toLowerCase().replace(/[^a-z0-9]/g, '');
        const p = products.find(
          (item) => item?.name?.toLowerCase().replace(/[^a-z0-9]/g, '') === urlName,
        );

        if (p) {
          if (p.accessMode === "waitlist" && !p.showPdp) {
            window.history.replaceState(null, "", "/");
            setCurrentPath("/");
            setViewingPdpProduct(null);
            return;
          }
          const cleanUrlName = p.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase().replace(/-+/g, '-').replace(/^-|-$/g, '');
          if (decodeURIComponent(path.substring(1)) !== cleanUrlName) {
            window.history.replaceState(null, "", `/${cleanUrlName}${window.location.search}${window.location.hash}`);
          }
          trackEcommerceEvent("view_item", p);
          setViewingPdpProduct(p);
          window.scrollTo(0, 0);
          return;
        } else {
          setViewingPdpProduct(null);
        }
      } else {
        setViewingPdpProduct(null);
      }
      
      if (hash) {
        const targetId = hash.substring(1);
        setTimeout(() => {
          const el = document.getElementById(targetId);
          if (el) {
             const yOffset = -80;
             const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
             window.scrollTo({top: y, behavior: 'smooth'});
          }
        }, 150);
      }
    };

    checkUrlForPdp();
    window.addEventListener("popstate", checkUrlForPdp);
    
    // Listen for hashchange as well
    const handleHashChange = () => {
       const hash = window.location.hash;
       if (hash) {
         const targetId = hash.substring(1);
         const el = document.getElementById(targetId);
         if (el) {
            const yOffset = -80;
            const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
            window.scrollTo({top: y, behavior: 'smooth'});
         }
       }
    };
    window.addEventListener("hashchange", handleHashChange);
    
    return () => {
      window.removeEventListener("popstate", checkUrlForPdp);
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [products, isLoadingProducts]);

  const handleHeroClick = (targetLink: string | undefined, sourceLabel: string) => {
    if (!targetLink) return;
    
    // Log the click
    if (targetLink !== "/waitlist" && targetLink !== "#waitlist") {
      trackEvent("click_hero_cta", { source: sourceLabel, link: targetLink });
    } else {
      trackEvent("click_join_waitlist", { source: sourceLabel });
    }

    if (targetLink === "/waitlist" || targetLink === "#waitlist") {
      // setIsWaitlistOpen(true);
    } else if (targetLink.startsWith("http")) {
      window.open(targetLink, "_blank");
    } else if (targetLink.startsWith("#")) {
      if (viewingPdpProduct) {
         navigateToPdp(null); // Return to home first
         setTimeout(() => { window.location.hash = targetLink.substring(1); }, 50);
      } else {
        const el = document.getElementById(targetLink.substring(1));
        if (el) {
           const yOffset = -80;
           const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
           window.scrollTo({top: y, behavior: 'smooth'});
        } else {
           window.location.hash = targetLink.substring(1);
        }
      }
    } else {
      const cleanLink = targetLink.split("?")[0];
      window.history.pushState({}, "", cleanLink);
      setCurrentPath(cleanLink);
      window.dispatchEvent(new Event("popstate"));
    }
  };

  const scrollToSeasonalDrops = (sourceLabel: string = "hero_banner") => {
    trackEvent("click_hero_cta", { source: sourceLabel, target: "seasonal_drops" });
    
    if (viewingPdpProduct) {
      navigateToPdp(null); // Return to home first
    }

    const performScroll = () => {
      const seasonalCategory = categories.find(c => 
        c.name && c.name.toLowerCase().includes("seasonal")
      ) || categories.find(c => 
        c.name && c.name.toLowerCase().includes("drop")
      ) || categories[0];

      const targetId = seasonalCategory ? `category-${seasonalCategory.id}` : null;
      let el = targetId ? document.getElementById(targetId) : null;
      
      if (!el) {
        el = document.querySelector('[id^="category-"]') || 
             document.getElementById("shop") || 
             document.getElementById("drops");
      }

      if (el) {
        const yOffset = -80;
        const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
        if ('scrollBehavior' in document.documentElement.style) {
          window.scrollTo({ top: y, behavior: 'smooth' });
        } else {
          window.scrollTo(0, y);
        }
      } else {
        const targetY = window.innerHeight - 80;
        if ('scrollBehavior' in document.documentElement.style) {
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        } else {
          window.scrollTo(0, targetY);
        }
      }
    };

    if (viewingPdpProduct) {
      setTimeout(performScroll, 150);
    } else {
      performScroll();
    }
  };

  const [canScrollCarousel, setCanScrollCarousel] = useState(false);
  const carouselTrackRef = useRef<HTMLDivElement>(null);

  // OTP States
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [resendTimer, setResendTimer] = useState(0);

  // Promo Popup State
  const [showPromoPopup, setShowPromoPopup] = useState(false);

  useEffect(() => {
    if (isLoadingCarousel) return;

    const hasSeenPromo = sessionStorage.getItem("hasSeenPromoPopup");
    if (!hasSeenPromo) {
      // const timer = setTimeout(() => {
      //  setShowPromoPopup(true);
      //  sessionStorage.setItem("hasSeenPromoPopup", "true");
      // }, 1000);
      // return () => clearTimeout(timer);
    }
  }, [isLoadingCarousel]);

  const cleanupRecaptcha = () => {
    // Do NOTHING. Keep the RecaptchaVerifier singleton alive forever.
    // Destroying and recreating it causes auth/network-request-failed in Firebase.
  };

  const closeWaitlist = () => {
    if (!isWaitlistSuccess) {
      trackEvent("abandon_waitlist", { step: showOtpInput ? "otp" : "form" });
      trackEvent(showOtpInput ? "abandon_waitlist_otp" : "abandon_waitlist_form");
    }
    setIsWaitlistOpen(false);
    setTimeout(() => {
      setIsWaitlistSuccess(false);
      setShowOtpInput(false);
      cleanupRecaptcha();
    }, 300);
  };

  useEffect(() => {
    let interval: any;
    if (showOtpInput && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showOtpInput, resendTimer]);

  useEffect(() => {
    if (isWaitlistOpen && !isWaitlistSuccess) {
      if (showOtpInput) {
        trackEvent("waitlist_step_otp_view");
      } else {
        trackEvent("waitlist_step_form_view");
      }
    }
  }, [isWaitlistOpen, showOtpInput, isWaitlistSuccess]);

  // Lock body scroll when any modal/popup is open
  useEffect(() => {
    if (isWaitlistOpen || showPromoPopup || activeModal || isOrderModalOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isWaitlistOpen, showPromoPopup, activeModal, isOrderModalOpen]);

  const handleResendOtp = async () => {
    trackEvent("resend_otp");
    setOtpError("");
    try {
      const { auth } = await import("./firebase");
      const { RecaptchaVerifier, signInWithPhoneNumber } = await import("firebase/auth");

      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          { size: "invisible" }
        );
        await window.recaptchaVerifier.render();
      }

      let formattedPhone = waitlistPhone.trim();
      if (!formattedPhone.startsWith("+")) {
        formattedPhone = `+91${formattedPhone}`;
      }
      const confirmation = await Promise.race([
        signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier),
         new Promise((_, reject) => setTimeout(() => reject(new Error("auth/network-request-failed")), 15000))
      ]);
      setConfirmationResult(confirmation);
      setResendTimer(30);
    } catch (error: any) {
      console.error("Resend OTP Error:", error?.code, error?.message);

      cleanupRecaptcha();

      if (error.code === "auth/too-many-requests") {
        setOtpError("Too many requests. Please wait before trying again.");
      } else if (
        error.message?.includes("Indexed Database") ||
        error.code === "auth/internal-error"
      ) {
        setOtpError(
          "Browser restrictions prevented login. Ensure cookies are enabled.",
        );
      } else {
        setOtpError("Failed to resend OTP. Please try again later.");
      }
    }
  };

  const checkCarouselScroll = () => {
    if (carouselTrackRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = carouselTrackRef.current;
      setCanScrollCarousel(
        scrollWidth > clientWidth &&
          Math.ceil(scrollLeft + clientWidth) < scrollWidth - 10,
      );
    }
  };

  useEffect(() => {
    checkCarouselScroll();
    window.addEventListener("resize", checkCarouselScroll);
    return () => window.removeEventListener("resize", checkCarouselScroll);
  }, []);

  useEffect(() => {
    const fetchCarousel = async () => {
      try {
        const res = await fetch("/api/carousel");
        const json = await res.json();
        if (json.success && json.data && json.data.length > 0) {
          setCarouselItems(json.data);
        }
      } catch (err) {
        console.error("Failed to fetch carousel:", err);
      } finally {
        setIsLoadingCarousel(false);
      }
    };
    
    const fetchRibbon = async () => {
      try {
        const res = await fetch("/api/ribbon");
        const json = await res.json();
        if (json.success && json.data && json.data.length > 0) {
          setRibbonItems(json.data);
        }
      } catch (err) {
        console.error("Failed to fetch ribbon items:", err);
      }
    };

    fetchCarousel();
    fetchRibbon();
  }, []);

  useEffect(() => {
    let triggered = false;
    const fetchProducts = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 30000); // 30 sec timeout

      try {
        const res = await fetch(`/api/products?t=${Date.now()}`, { signal: controller.signal });
        clearTimeout(timeoutId);
        const json = await res.json();
        let loadedProducts = DEFAULT_PRODUCTS;
        if (json.success && json.data && json.data.length > 0) {
          loadedProducts = json.data;
          setProducts(json.data);
        } else {
          setProducts(DEFAULT_PRODUCTS);
        }
        
        // update viewing product immediately to avoid home screen flash
        if (typeof window !== "undefined") {
          const path = window.location.pathname;
          if (path !== "/" && path.length > 1 && path !== "/admin_access" && path !== "/terms" && path !== "/privacy" && path !== "/refund") {
            const urlName = decodeURIComponent(path.substring(1)).toLowerCase().replace(/[^a-z0-9]/g, '');
            const p = loadedProducts.find(item => item?.name?.toLowerCase().replace(/[^a-z0-9]/g, '') === urlName);
            if (p) {
              if (p.accessMode === "waitlist" && !p.showPdp) {
                window.history.replaceState(null, "", "/");
                setCurrentPath("/");
                setViewingPdpProduct(null);
              } else {
                const cleanUrlName = p.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase().replace(/-+/g, '-').replace(/^-|-$/g, '');
                if (decodeURIComponent(path.substring(1)) !== cleanUrlName) {
                  window.history.replaceState(null, "", `/${cleanUrlName}${window.location.search}${window.location.hash}`);
                }
                setViewingPdpProduct(p);
              }
            }
          }
        }
      } catch (err) {
        console.error("Failed to fetch products:", err);
        setProducts(DEFAULT_PRODUCTS);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    const fetchCollections = async () => {
      try {
        const res = await fetch("/api/collections");
        const json = await res.json();
        if (json.success && json.data && json.data.length > 0) {
          setCollectionsItems(json.data);
        }
      } catch (err) {
        console.error("Failed to fetch collections:", err);
      } finally {
        setIsLoadingCollections(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const res = await fetch("/api/categories");
        const json = await res.json();
        if (json.success && json.data) {
          setCategories(json.data);
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchProducts();
    fetchCategories();
    setTimeout(fetchCollections, 600); // Stagger collections slightly

  }, []);

  useEffect(() => {
    if (collectionsItems.length <= 4) return;
    const container = collectionsScrollRef.current;
    const firstSet = firstSetRef.current;
    const secondSet = secondSetRef.current;

    if (!container || !firstSet || !secondSet) return;

    let animationFrameId: number;
    let lastTime = performance.now();
    let accumulator = 0;

    const scroll = (time: number) => {
      if (isAutoScrolling) {
        const deltaTime = time - lastTime;
        // ~60 pixels per second
        accumulator += (deltaTime * 60) / 1000;

        if (accumulator >= 1) {
          const pixels = Math.floor(accumulator);
          container.scrollLeft += pixels;
          accumulator -= pixels;

          const loopDist = secondSet.offsetLeft - firstSet.offsetLeft;
          if (container.scrollLeft >= loopDist) {
            container.scrollLeft -= loopDist;
          }
        }
      } else {
        // Reset accumulator to prevent jumping after pausing
        accumulator = 0;
      }
      lastTime = time;
      animationFrameId = requestAnimationFrame(scroll);
    };

    animationFrameId = requestAnimationFrame(scroll);

    return () => cancelAnimationFrame(animationFrameId);
  }, [collectionsItems.length, isAutoScrolling]);

  useEffect(() => {
    if (carouselItems.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }, 8000);

    return () => clearInterval(timer);
  }, [carouselItems, currentSlide]);

  // If Stock > 0 & Launch is NULL -> Live Now
  const liveProducts = products.filter((p) => p.inventory > 0 && !p.dropDate);
  // If Stock is > 0 & Launch is NOT NULL -> Upcoming Drops (showing stock instead of 20)
  // Or if Stock <= 0 & Launch is NOT NULL -> Upcoming Drops (this logic stays for future/empty stock items having a drop date)
  const upcomingProducts = products.filter((p) => p.dropDate);
  const soldOutProducts = products.filter(
    (p) => p.inventory <= 0 && !p.dropDate,
  );

  const productsForStats = products.filter(
    (p) => p.dropDate && p.inventory > 0,
  );
  const collectionCount = productsForStats.length;
  const totalPieces = productsForStats.reduce((acc, p) => acc + p.inventory, 0);

  // Safe fallback if products haven't loaded yet or are empty
  const defaultFallback = {
    id: 0,
    name: "General Waitlist",
    notes: "",
    image: "",
    price: "",
  };
  const selectedProduct =
    products.find((p) => p.id === selectedProductId) ||
    upcomingProducts.find((p) => p.id === selectedProductId) ||
    upcomingProducts[0] ||
    products[0] ||
    DEFAULT_PRODUCTS[0] ||
    defaultFallback;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "50px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const observeElements = () => {
      document
        .querySelectorAll(".reveal-on-scroll:not(.is-visible)")
        .forEach((el) => {
          observer.observe(el);
        });
    };

    observeElements();
    // Re-check periodically for any new elements added by React transitions or state updates
    const intervalId = setInterval(observeElements, 100);

    return () => {
      clearInterval(intervalId);
      observer.disconnect();
    };
  }, [viewingPdpProduct, products]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name && email && !isSubmitting) {
      setIsSubmitting(true);
      try {
        const response = await fetch("/api/newsletter", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name, email }),
        });

        const data = await response.json();
        if (data.success) {
          trackEvent("join_newsletter");
          setSubscribed(true);
          setName("");
          setEmail("");
          setTimeout(() => setSubscribed(false), 3000);
        }
      } catch (error) {
        console.error("Newsletter error:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    if (isWaitlistOpen && !window.recaptchaVerifier) {
      import("./firebase").then(({ auth }) => {
        import("firebase/auth").then(({ RecaptchaVerifier }) => {
          try {
            window.recaptchaVerifier = new RecaptchaVerifier(
              auth,
              "recaptcha-container",
              { size: "invisible" },
            );
            window.recaptchaVerifier.render().catch((e: any) => {
              console.error("Recaptcha Render Error:", e);
              cleanupRecaptcha();
            });
          } catch (e: any) {
            console.error("Recaptcha Init Error:", e);
            cleanupRecaptcha();
          }
        });
      });
    }
  }, [isWaitlistOpen]);

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWaitlistError("");
    setIsSubmittingWaitlist(true);

    if (!showOtpInput) {
      if (waitlistPhone.replace(/\D/g, "").length < 10) {
        setWaitlistError("Please enter a valid phone number.");
        setIsSubmittingWaitlist(false);
        return;
      }

      try {
        // Pre-check for duplicates
        const checkEndpoint = selectedProduct?.inviteOnly ? "/api/invitelist/check" : "/api/waitlist/check";
        const checkRes = await fetch(checkEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: waitlistEmail, phone: waitlistPhone }),
        });
        const checkData = await checkRes.json();
        if (!checkData.success) {
          setWaitlistError(
            checkData.message || "You Are Already Added on The Waitlist",
          );
          setIsSubmittingWaitlist(false);
          return;
        }

        const { auth } = await import("./firebase");
        const { RecaptchaVerifier, signInWithPhoneNumber } = await import("firebase/auth");

        if (!window.recaptchaVerifier) {
          window.recaptchaVerifier = new RecaptchaVerifier(
            auth,
            "recaptcha-container",
            { size: "invisible" },
          );
          await window.recaptchaVerifier.render();
        }

        let formattedPhone = waitlistPhone.trim();
        if (!formattedPhone.startsWith("+")) {
          formattedPhone = `+91${formattedPhone}`;
        }

        console.log("Proceeding to signInWithPhoneNumber for:", formattedPhone);

        try {
          trackEvent("request_otp");
          trackEvent("waitlist_step_form_submit");
          const confirmation = await Promise.race([
            signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier),
            new Promise((_, reject) => setTimeout(() => reject(new Error("auth/network-request-failed")), 15000))
          ]);
          console.log("signInWithPhoneNumber resolved", confirmation);
          setConfirmationResult(confirmation);
          setShowOtpInput(true);
          setResendTimer(30);
        } catch (error: any) {
          console.timeEnd("firebase-sign-in");
          throw error;
        }
      } catch (error: any) {
        console.error("OTP Error:", error?.code, error?.message);

        cleanupRecaptcha();

        if (error.code === "auth/too-many-requests") {
          setWaitlistError(
            "Security block: Too many attempts from this IP. Please wait 20-30 mins or use a Test Number in Firebase Console.",
          );
        } else if (error.code === "auth/network-request-failed") {
          setWaitlistError(
            "Network error: Ensure this domain is whitelisted in Firebase Console (Authentication > Settings > Authorized Domains) and check your internet / ad-blocker.",
          );
        } else if (error.code === "auth/captcha-check-failed") {
          setWaitlistError(
            "Hostname Error: This domain is not whitelisted in Firebase Console. Go to Auth > Settings > Authorized Domains and add this URL.",
          );
        } else if (error.code === "auth/billing-not-enabled") {
          setWaitlistError(
            'Billing Error: You are on the Blaze plan, but you must ALSO upgrade to Identity Platform. Go to Firebase Auth > Settings and click "Upgrade to Google Cloud Identity Platform".',
          );
        } else if (error.code === "auth/invalid-phone-number") {
          setWaitlistError("Invalid phone number. Please check and try again.");
        } else if (
          error.message?.includes("Indexed Database") ||
          error.code === "auth/internal-error"
        ) {
          setWaitlistError(
            "Browser restrictions prevented login. Please ensure third-party cookies are enabled or try outside of Incognito mode.",
          );
        } else {
          setWaitlistError(
            error.message || "Error sending OTP. Ensure phone number is valid.",
          );
        }
      } finally {
        setIsSubmittingWaitlist(false);
      }
    } else {
      trackEvent("verify_otp", { item_name: selectedProduct.name });
      trackEvent("waitlist_step_otp_submit");
      setOtpError("");
      try {
        const trimmedOtp = otp.trim().replace(/\s/g, "");
        await confirmationResult.confirm(trimmedOtp);

        cleanupRecaptcha();

        let normalizedPhone = waitlistPhone.trim().replace(/\s+/g, "");
        if (!normalizedPhone.startsWith("+")) {
          normalizedPhone = `+91${normalizedPhone}`;
        }

        // OTP verified successfully, now submit to backend
        const postEndpoint = selectedProduct?.inviteOnly ? "/api/invitelist" : "/api/waitlist";
        const response = await fetch(postEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: waitlistName,
            email: waitlistEmail,
            phone: normalizedPhone,
            product: selectedProduct.name,
          }),
        });

        const data = await response.json();

        if (data.success) {
          trackEvent("join_waitlist", { item_name: selectedProduct.name });
          if (typeof window !== "undefined" && (window as any).fbq) {
            (window as any).fbq("track", "Lead", {
              content_name: "waitlist_success",
              item_name: selectedProduct.name
            });
          }
          setIsWaitlistSuccess(true);
          setWaitlistPosition((data.count || 0) + 101);
          setWaitlistName("");
          setWaitlistEmail("");
          setWaitlistPhone("");
          setShowOtpInput(false);
          setOtp("");
        } else {
          setOtpError(data.message || "Something went wrong on the server.");
        }
      } catch (error: any) {
        if (
          error?.code === "auth/invalid-verification-code" ||
          error?.message?.includes("invalid-verification-code")
        ) {
          setOtpError(
            "Incorrect verification code. Please check and try again.",
          );
        } else {
          setOtpError("Invalid OTP code. Please try again.");
        }
      } finally {
        setIsSubmittingWaitlist(false);
      }
    }
  };

  if (typeof window !== "undefined" && currentPath === "/admin_access") {
    return <AdminPanel />;
  }

  if (
    typeof window !== "undefined" &&
    (currentPath === "/terms" ||
      currentPath === "/privacy" ||
      currentPath === "/refund")
  ) {
    return <LegalPage path={currentPath} />;
  }

  return (
    <div className="bg-black text-neutral-50 antialiased selection:bg-amber-500/30 selection:text-amber-100 overflow-x-clip font-sans">
      <React.Suspense fallback={null}>
        <Helmet>
          <title>Kayoki | Luxury Fragrances</title>
          <link rel="canonical" href={`https://kayoki.shop${currentPath === '/' ? '' : currentPath}`} />
        <meta
          name="description"
          content="Discover Kayoki's exclusive collections of luxury fragrances, featuring limited edition collections and summer drops."
        />

        {/* Product List Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: products.map((product, index) => ({
              "@type": "ListItem",
              position: index + 1,
              item: {
                "@type": "Product",
                name: product.name,
                description: `Kayoki Luxury fragrance featuring notes of ${product.notes}.`,
                image: product.image,
                brand: {
                  "@type": "Brand",
                  name: "Kayoki",
                },
                offers: {
                  "@type": "Offer",
                  price: product.price.replace(/[^0-9.-]+/g, ""),
                  priceCurrency: "INR",
                  availability:
                    product.inventory > 0
                      ? "https://schema.org/InStock"
                      : "https://schema.org/OutOfStock",
                },
              },
            })),
            name: "Kayoki Products List",
          })}
        </script>

        {/* WebSite / Organization Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Kayoki",
            url: "https://kayoki.shop/",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://kayoki.shop/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Kayoki",
            url: "https://kayoki.shop/",
            logo: "https://lh3.googleusercontent.com/d/1TCdcvg9wbkIdRRDTR_99CO6gFEYvKHxR",
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+91 9999999999",
              contactType: "customer service",
            },
          })}
        </script>

        {/* Home Breadcrumb List (Google Schema JSON-LD) */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://kayoki.shop/",
              },
            ],
          })}
        </script>
      </Helmet>
      {/* Navigation */}
      <nav
        className={`fixed transition-colors duration-300 md:px-12 flex z-[70] w-full pr-6 pl-6 items-center justify-center h-[64px] md:h-[72px] top-0 ${viewingPdpProduct ? "bg-black shadow-sm" : isScrolled ? "max-md:bg-black md:bg-black/40 md:backdrop-blur-md max-md:shadow-sm" : "bg-transparent"}`}
      >
        <a
          href="#"
          aria-label="Kayoki Home"
          className="flex items-center justify-center cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            navigateToPdp(null);
          }}
        >
          {/* 
            Note: Upload your logo image to the 'public' folder and name it 'logo.png'.
            If your file has a different name, update the src attribute below.
          */}
          <img
            src="https://lh3.googleusercontent.com/d/1TCdcvg9wbkIdRRDTR_99CO6gFEYvKHxR"
            alt="Kayoki Logo"
            width="200"
            height="40"
            className={`h-8 md:h-10 w-auto object-contain transition-all duration-700`}
            referrerPolicy="no-referrer"
            fetchPriority="high"
          />
        </a>
      </nav>

      <main className="w-full flex-grow">
        <AnimatePresence
          mode="wait"
          onExitComplete={() => window.scrollTo({ top: 0, behavior: "instant" })}
        >
        {viewingPdpProduct ? (
          <motion.div
            key="pdp"
            initial={{ opacity: 0, scale: 0.99, y: 15 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
              transitionEnd: { transform: "none", willChange: "auto" },
            }}
            exit={{
              opacity: 0,
              scale: 0.99,
              y: 15,
              transition: { duration: 0.15, ease: "easeOut" },
            }}
            transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
            className="w-full relative z-[60]"
          >
            <React.Suspense fallback={<PdpSkeleton />}>
              <Pdp
                product={viewingPdpProduct}
                allProducts={products}
                categories={categories}
                onBack={() => navigateToPdp(null)}
                onNavigateToProduct={(p) => navigateToPdp(p)}
                onWaitlist={() => {
                  trackEvent("click_join_waitlist", { item_name: viewingPdpProduct.name, source: "pdp" });
                  setSelectedProductId(viewingPdpProduct.id);
                  setIsWaitlistOpen(true);
                }}
                onBuyNow={() => {
                  trackEcommerceEvent("begin_checkout", viewingPdpProduct, { source: "pdp" });
                  setSelectedProductId(viewingPdpProduct.id);
                  setIsOrderModalOpen(true);
                }}
              />
            </React.Suspense>
          </motion.div>
        ) : isLoadingProducts &&
          currentPath !== "/" &&
          currentPath.length > 1 ? (
          <motion.div
            key="pdp-skeleton-loading"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10, transition: { duration: 0.15 } }}
            transition={{ duration: 0.4 }}
            className="w-full relative z-[60]"
          >
            <PdpSkeleton />
          </motion.div>
        ) : (
          <motion.div
            key="home"
            initial={{ opacity: 0, scale: 1.01, y: -15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{
              opacity: 0,
              scale: 1.01,
              y: -15,
              transition: { duration: 0.15, ease: "easeOut" },
            }}
            transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
            className="w-full"
          >
            <>
              {/* Hero Section / Carousel */}
              <header className="relative w-full overflow-hidden mt-[64px] md:mt-[72px] bg-neutral-900 group">
                   {/* Mobile & Desktop Hero Video */}
                <div className="w-full relative group">
                  {/* Mobile Media */}
                  <div 
                    className="block md:hidden w-full relative cursor-pointer aspect-[4/6] bg-neutral-900"
                    onClick={() => scrollToSeasonalDrops("hero_mobile_banner")}
                  >
                    {mobileHeroVideoUrl ? (
                      isMobileMediaImage ? (
                        <img
                          src={mobileHeroVideoUrl}
                          alt="Mobile Campaign Banner"
                          fetchPriority="high"
                          loading="eager"
                          onLoad={() => setMobileVideoReady(true)}
                          className="w-full h-full absolute inset-0 object-cover block z-10"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            console.error("Mobile hero image load error");
                            setMobileVideoError({
                              code: 4,
                              message: "Image failed to load. Make sure the link is a valid image URL.",
                            });
                          }}
                        />
                      ) : (
                        <video
                          src={mobileHeroVideoUrl}
                          autoPlay
                          loop
                          muted
                          playsInline
                          preload="auto"
                          poster="https://lh3.googleusercontent.com/d/1CTfppBA9CeUpEfaeNpHPmvbH_8RssODj"
                          onLoadedData={() => setMobileVideoReady(true)}
                          onCanPlay={() => setMobileVideoReady(true)}
                          className={`w-full object-cover block transition-opacity duration-500 ${mobileVideoReady ? 'relative z-10 opacity-100 h-auto' : 'absolute inset-0 opacity-0 z-0 invisible h-full'}`}
                          onError={(e) => {
                            const err = e.currentTarget.error;
                            console.error("Mobile hero video error:", err);
                            setMobileVideoError({
                              code: err ? err.code : 4,
                              message: err ? err.message : "Video source failed or proxy error",
                            });
                          }}
                        />
                      )
                    ) : null}
                    
                    {!isMobileMediaImage && (
                      <img 
                        src="https://lh3.googleusercontent.com/d/1CTfppBA9CeUpEfaeNpHPmvbH_8RssODj" 
                        alt="Loading Campaign..." 
                        fetchPriority="high"
                        loading="eager"
                        className={`w-full object-cover transition-opacity duration-500 ${
                          mobileVideoReady ? 'absolute inset-0 h-full z-0 opacity-0 pointer-events-none' : 'absolute inset-0 z-10 block h-full opacity-100'
                        }`} 
                      />
                    )}
                    {isDevelopment && mobileVideoError && (
                      <div className="absolute top-4 left-4 right-4 bg-red-950/95 border border-red-500/50 rounded p-4 text-red-200 font-mono text-xs z-50 shadow-xl backdrop-blur-md">
                        <div className="font-bold flex items-center gap-2 mb-1.5 text-red-400">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></span>
                          [DEV MODE] Mobile Media Load Failed
                        </div>
                        <div className="space-y-1 text-[11px] text-red-300">
                          <p>Error Type: <span className="font-bold text-white bg-red-900/50 px-1 py-0.5 rounded">{isMobileMediaImage ? "Image" : "Video"}</span></p>
                          <p>Message: <span className="text-white bg-red-900/50 px-1 py-0.5 rounded">{mobileVideoError.message || "Unknown"}</span></p>
                          <p className="mt-2 text-[10px] text-neutral-400 break-all select-all select-text cursor-text bg-black/30 p-1 rounded font-sans">URL: {mobileHeroVideoUrl}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Desktop Media */}
                  <div 
                    className="hidden md:block w-full relative cursor-pointer aspect-[16/9] bg-neutral-900"
                    onClick={() => scrollToSeasonalDrops("hero_desktop_banner")}
                  >
                    {desktopHeroVideoUrl ? (
                      isDesktopMediaImage ? (
                        <img
                          src={desktopHeroVideoUrl}
                          alt="Desktop Campaign Banner"
                          fetchPriority="high"
                          loading="eager"
                          onLoad={() => setDesktopVideoReady(true)}
                          className="w-full h-full absolute inset-0 object-cover block z-10"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            console.error("Desktop hero image load error");
                            setDesktopVideoError({
                              code: 4,
                              message: "Image failed to load. Make sure the link is a valid image URL.",
                            });
                          }}
                        />
                      ) : (
                        <video
                          src={desktopHeroVideoUrl}
                          autoPlay
                          loop
                          muted
                          playsInline
                          preload="auto"
                          poster="https://lh3.googleusercontent.com/d/1CTfppBA9CeUpEfaeNpHPmvbH_8RssODj"
                          onLoadedData={() => setDesktopVideoReady(true)}
                          onCanPlay={() => setDesktopVideoReady(true)}
                          className={`w-full object-cover block transition-opacity duration-500 ${desktopVideoReady ? 'relative z-10 opacity-100 h-auto' : 'absolute inset-0 opacity-0 z-0 invisible h-full'}`}
                          onError={(e) => {
                            const err = e.currentTarget.error;
                            console.error("Desktop hero video error:", err);
                            setDesktopVideoError({
                              code: err ? err.code : 4,
                              message: err ? err.message : "Video source failed or proxy error",
                            });
                          }}
                        />
                      )
                    ) : null}
                    
                    {!isDesktopMediaImage && (
                      <img 
                        src="https://lh3.googleusercontent.com/d/1CTfppBA9CeUpEfaeNpHPmvbH_8RssODj" 
                        alt="Loading Campaign..." 
                        fetchPriority="high"
                        loading="eager"
                        className={`w-full object-cover transition-opacity duration-500 ${
                          desktopVideoReady ? 'absolute inset-0 h-full z-0 opacity-0 pointer-events-none' : 'absolute inset-0 z-10 block h-full opacity-100'
                        }`} 
                      />
                    )}
                    {isDevelopment && desktopVideoError && (
                      <div className="absolute top-4 left-4 right-4 bg-red-950/95 border border-red-500/50 rounded p-4 text-red-200 font-mono text-xs z-50 shadow-xl backdrop-blur-md">
                        <div className="font-bold flex items-center gap-2 mb-1.5 text-red-400">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></span>
                          [DEV MODE] Desktop Media Load Failed
                        </div>
                        <div className="space-y-1 text-[11px] text-red-300">
                          <p>Error Type: <span className="font-bold text-white bg-red-900/50 px-1 py-0.5 rounded">{isDesktopMediaImage ? "Image" : "Video"}</span></p>
                          <p>Message: <span className="text-white bg-red-900/50 px-1 py-0.5 rounded">{desktopVideoError.message || "Unknown"}</span></p>
                          <p className="mt-2 text-[10px] text-neutral-400 break-all select-all select-text cursor-text bg-black/30 p-1 rounded font-sans">URL: {desktopHeroVideoUrl}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Reuse first carousel item's text if available */}
                  {!isLoadingCarousel && carouselItems.length > 0 && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center z-10 text-center px-4 pointer-events-none">
                      {carouselItems[0].heading && (
                        <h1 className="text-[1.1rem] font-bold tracking-[0.15em] uppercase text-white mb-2 drop-shadow-lg font-sans">
                          {carouselItems[0].heading}
                        </h1>
                      )}
                      {carouselItems[0].subheading && (
                        <p className="text-[0.65rem] font-bold tracking-[0.10em] uppercase text-white drop-shadow-md mb-8">
                          {carouselItems[0].subheading}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Experience Now CTA - Mobile */}
                  <div className="md:hidden absolute bottom-8 w-full flex justify-center z-20 pointer-events-auto">
                    <button
                      onClick={() => scrollToSeasonalDrops("hero_mobile_experience_now")}
                      className="text-black border-b border-black pb-1 font-sans font-light tracking-wide text-sm hover:opacity-80 transition-opacity cursor-pointer bg-transparent"
                    >
                      {mobileCarouselItem?.ctaText || "Experience Now"}
                    </button>
                  </div>

                  {/* Experience Now CTA - Desktop */}
                  <div className="hidden md:flex absolute bottom-12 w-full justify-center z-20 pointer-events-auto">
                    <button
                      onClick={() => scrollToSeasonalDrops("hero_desktop_experience_now")}
                      className="text-white border-b border-white pb-1 font-sans font-light tracking-wide text-base hover:opacity-80 transition-opacity cursor-pointer bg-transparent"
                    >
                      {desktopCarouselItem?.ctaText || "Experience Now"}
                    </button>
                  </div>
                </div>

                {/* Desktop Carousel Removed */}
                {false && (
                <div className="hidden w-full h-full">
                  {isLoadingCarousel ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-full h-full bg-neutral-900 animate-pulse"></div>
                    </div>
                  ) : carouselItems.length > 0 ? (
                    <motion.div
                      className="relative w-full h-full touch-pan-y"
                      drag="x"
                      dragConstraints={{ left: 0, right: 0 }}
                      dragElastic={0.2}
                      onDragEnd={(e, { offset }) => {
                        if (offset.x < -50) {
                          setCurrentSlide(
                            (prev) => (prev + 1) % carouselItems.length,
                          );
                        } else if (offset.x > 50) {
                          setCurrentSlide(
                            (prev) =>
                              (prev - 1 + carouselItems.length) %
                              carouselItems.length,
                          );
                        }
                      }}
                    >
                      <motion.div
                        className="flex w-full h-full"
                        initial={false}
                        animate={{ x: `-${currentSlide * 100}%` }}
                        transition={{
                          type: "tween",
                          ease: "easeInOut",
                          duration: 0.8,
                        }}
                      >
                        {carouselItems.map((item, index) => (
                          <div
                            key={index}
                            className="relative w-full h-full flex-shrink-0"
                          >
                            <img
                              loading="lazy"
                              src={item.image || undefined}
                              alt={item.heading || "Kayoki Collection"}
                              className="w-full h-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                            <div className="bg-black/30 absolute inset-0 text-left"></div>
                            <div className="bg-gradient-to-t from-black via-transparent to-transparent absolute inset-0"></div>

                            <div className="absolute inset-0 flex flex-col items-center justify-center z-10 text-center px-4 pointer-events-none">
                              {item.heading && (
                                <motion.h2
                                  initial={{ y: 20, opacity: 0 }}
                                  animate={
                                    currentSlide === index
                                      ? { y: 0, opacity: 1 }
                                      : { y: 20, opacity: 0 }
                                  }
                                  transition={{
                                    duration: 0.8,
                                    delay: currentSlide === index ? 0.3 : 0,
                                  }}
                                  className="text-[1.1rem] md:text-[2.2rem] font-bold tracking-[0.15em] uppercase text-white mb-2 drop-shadow-lg font-sans"
                                >
                                  {item.heading}
                                </motion.h2>
                              )}

                              {item.subheading && (
                                <motion.p
                                  initial={{ y: 20, opacity: 0 }}
                                  animate={
                                    currentSlide === index
                                      ? { y: 0, opacity: 1 }
                                      : { y: 20, opacity: 0 }
                                  }
                                  transition={{
                                    duration: 0.8,
                                    delay: currentSlide === index ? 0.4 : 0,
                                  }}
                                  className="text-[0.65rem] md:text-sm font-bold tracking-[0.10em] uppercase text-white drop-shadow-md mb-8"
                                >
                                  {item.subheading}
                                </motion.p>
                              )}

                              {item.ctaText && (
                                <motion.button
                                  initial={{ scale: 0.9, opacity: 0 }}
                                  animate={
                                    currentSlide === index
                                      ? { scale: 1, opacity: 1 }
                                      : { scale: 0.9, opacity: 0 }
                                  }
                                  transition={{
                                    duration: 0.8,
                                    delay: currentSlide === index ? 0.5 : 0,
                                  }}
                                  onClick={() => {
                                    const targetLink = item.ctaLink || item.link;
                                    if (
                                      targetLink &&
                                      targetLink !== "/waitlist" &&
                                      targetLink !== "#waitlist"
                                    ) {
                                      trackEvent("click_hero_cta", { source: "hero_carousel_cta", link: targetLink, item_name: item.heading, carousel_index: index });
                                      if (targetLink.startsWith("http")) {
                                        window.open(targetLink, "_blank");
                                      } else if (targetLink.startsWith("#")) {
                                        const el = document.getElementById(targetLink.substring(1));
                                        if (el) {
                                           const yOffset = -80;
                                           const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
                                           window.scrollTo({top: y, behavior: 'smooth'});
                                        }
                                      } else {
                                        const cleanLink = targetLink.split("?")[0];
                                        window.history.pushState(
                                          {},
                                          "",
                                          cleanLink,
                                        );
                                        setCurrentPath(cleanLink);
                                        window.dispatchEvent(
                                          new Event("popstate"),
                                        );
                                      }
                                    } else {
                                      trackEvent("click_join_waitlist", { source: "hero_carousel_cta", item_name: item.heading, carousel_index: index });
                                      // setIsWaitlistOpen(true);
                                    }
                                  }}
                                  className="pointer-events-auto animate-glass-shine bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-3 text-sm md:text-base font-semibold tracking-wider hover:bg-white/20 transition-all rounded-full uppercase cursor-pointer"
                                >
                                  {item.ctaText}
                                </motion.button>
                              )}
                            </div>
                          </div>
                        ))}
                      </motion.div>
                    </motion.div>
                  ) : (
                    <div className="absolute inset-0">
                      <img
                        loading="lazy"
                        src="https://lh3.googleusercontent.com/d/1BJj9GM13McumAOIsl1ETkX_Lr-DAjCbL"
                        alt="Kayoki Summer Collection"
                        className="w-full h-full object-cover opacity-90"
                        referrerPolicy="no-referrer"
                      />
                      <div className="bg-black/30 absolute inset-0"></div>
                      <div className="bg-gradient-to-t from-black via-transparent to-transparent absolute inset-0"></div>
                      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 text-center px-4">
                        <h1 className="text-[1.1rem] md:text-[1.75rem] font-bold tracking-[0.15em] uppercase text-white mb-2 drop-shadow-md font-sans">
                          Summer Collection
                        </h1>
                        <p className="text-[0.65rem] md:text-sm font-bold tracking-[0.10em] uppercase text-white drop-shadow-md border-b-0 border-transparent">
                          Launching 20th June 2026
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Carousel Indicators */}
                  {!isLoadingCarousel && carouselItems.length > 1 && (
                    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                      {carouselItems.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentSlide(i)}
                          className={`cursor-pointer h-1 transition-all duration-500 ease-out flex-shrink-0 ${i === currentSlide ? "w-8 bg-white" : "w-2 bg-white/30"}`}
                          aria-label={`Go to slide ${i + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
                )}
              </header>

              {/* Urgency Marquee */}
              <div className="border-y border-white/10 py-4 overflow-hidden bg-black flex w-full">
                <div className="animate-marquee flex items-center whitespace-nowrap shrink-0">
                  {[...Array(6)].map((_, i) => (
                    <div key={`a-${i}`} className="flex items-center">
                      {ribbonItems.map((item, index) => (
                         <React.Fragment key={`a-item-${i}-${index}`}>
                           <span className="px-10 md:px-16 text-[0.6rem] md:text-[0.65rem] tracking-[0.25em] uppercase font-medium text-neutral-400">
                             {item.text}
                           </span>
                           <span className="text-amber-500/80 text-xs md:text-sm">
                             •
                           </span>
                         </React.Fragment>
                      ))}
                    </div>
                  ))}
                </div>
                <div
                  className="animate-marquee flex items-center whitespace-nowrap shrink-0"
                  aria-hidden="true"
                >
                  {[...Array(6)].map((_, i) => (
                    <div key={`b-${i}`} className="flex items-center">
                      {ribbonItems.map((item, index) => (
                         <React.Fragment key={`b-item-${i}-${index}`}>
                           <span className="px-10 md:px-16 text-[0.6rem] md:text-[0.65rem] tracking-[0.25em] uppercase font-medium text-neutral-400">
                             {item.text}
                           </span>
                           <span className="text-amber-500/80 text-xs md:text-sm">
                             •
                           </span>
                         </React.Fragment>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Shop Now Section (Live Products) */}
              {isLoadingProducts ? (
                <section className="py-12 md:py-16 px-6 md:px-12 max-w-[100rem] mx-auto">
                  <div className="flex flex-col md:flex-row justify-between items-start mb-8 md:mb-12 gap-6 reveal-on-scroll">
                    <div className="w-full">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-8 bg-neutral-800 rounded w-48 animate-pulse"></div>
                      </div>
                      <div className="h-4 bg-neutral-800 rounded w-full max-w-md animate-pulse"></div>
                    </div>
                  </div>
                  <div className="flex overflow-x-hidden gap-6 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-x-6 md:gap-y-12 pb-4 md:pb-0">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="min-w-[65vw] md:min-w-0 md:w-auto animate-pulse flex-shrink-0"
                      >
                        <div className="aspect-[3/4] bg-neutral-800 rounded-xl mb-6"></div>
                        <div className="flex justify-between items-start">
                          <div className="w-2/3">
                            <div className="h-4 bg-neutral-800 rounded mb-2 w-3/4"></div>
                            <div className="h-3 bg-neutral-800 rounded w-1/2"></div>
                          </div>
                          <div className="h-4 bg-neutral-800 rounded w-12"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ) : (
                categories.length > 0 ? (
                  categories.map((category) => {
                    const categoryProducts = products.filter((p) => {
                      if (p.categoryId === category.id) return true;
                      if (
                        category.productIds &&
                        (category.productIds.includes(Number(p.id)) ||
                          category.productIds.includes(String(p.id)))
                      ) {
                        return true;
                      }
                      return false;
                    });

                    if (categoryProducts.length === 0) return null;

                    const words = (category.name || "").trim().split(/\s+/);
                    const lastWord = words.pop() || "";
                    const remainingText = words.join(" ");

                    const renderRemainingText = (text: string) => {
                      if (!text) return null;
                      if (text.startsWith("T")) {
                        const rest = text.slice(1);
                        return (
                          <>
                            <span className="relative inline-block">
                              <motion.svg
                                className="absolute -top-[0.55em] -left-[0.3em] w-[0.75em] h-[0.75em] pointer-events-none filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]"
                                viewBox="0 0 64 64"
                                initial={{ opacity: 0, y: -25, rotate: -35, scale: 0.6 }}
                                whileInView={{ 
                                  opacity: 1, 
                                  scale: 1,
                                }}
                                viewport={{ once: true }}
                                animate={{
                                  y: [0, -4, 0],
                                  rotate: [-15, -11, -19, -15],
                                }}
                                transition={{
                                  default: {
                                    type: "spring",
                                    stiffness: 70,
                                    damping: 15,
                                    delay: 0.3
                                  },
                                  y: {
                                    duration: 3.5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                  },
                                  rotate: {
                                    duration: 5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                  },
                                }}
                              >
                                <defs>
                                  {/* Rich metallic gold gradient for front surfaces */}
                                  <linearGradient id="crown-gold-front" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#FFEAA5" />
                                    <stop offset="30%" stopColor="#DFB37A" />
                                    <stop offset="70%" stopColor="#B38642" />
                                    <stop offset="100%" stopColor="#8C6027" />
                                  </linearGradient>
                                  
                                  {/* Darker metallic gold for back surfaces & shadows to create 3D depth */}
                                  <linearGradient id="crown-gold-back" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#A87F3B" />
                                    <stop offset="50%" stopColor="#7A531C" />
                                    <stop offset="100%" stopColor="#4D310A" />
                                  </linearGradient>
                                  
                                  {/* Ruby Red gradient for the velvet inner cap/jewels */}
                                  <radialGradient id="crown-ruby" cx="35%" cy="35%" r="65%">
                                    <stop offset="0%" stopColor="#FF4D6D" />
                                    <stop offset="40%" stopColor="#C9184A" />
                                    <stop offset="100%" stopColor="#590D22" />
                                  </radialGradient>

                                  {/* Pearl/Diamond highlight gradient */}
                                  <radialGradient id="crown-pearl" cx="35%" cy="35%" r="65%">
                                    <stop offset="0%" stopColor="#FFFFFF" />
                                    <stop offset="60%" stopColor="#E2E8F0" />
                                    <stop offset="100%" stopColor="#94A3B8" />
                                  </radialGradient>
                                </defs>

                                {/* 1. The Velvet Cap (creates deep 3D background inside the crown) */}
                                <path d="M12,42 C12,28 52,28 52,42 C52,46 12,46 12,42 Z" fill="url(#crown-ruby)" opacity="0.85" />

                                {/* 2. Back rim peaks (dark gold, visible behind the front) */}
                                <path d="M18,36 L24,20 L30,34 L32,34 L38,20 L44,36" fill="none" stroke="url(#crown-gold-back)" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                                
                                {/* Back peak tips/pearls */}
                                <circle cx="24" cy="20" r="2.5" fill="url(#crown-pearl)" />
                                <circle cx="38" cy="20" r="2.5" fill="url(#crown-pearl)" />

                                {/* 3. Dynamic Arches (sweeping from sides to center) */}
                                <path d="M14,41 C18,22 32,20 32,20 C32,20 46,22 50,41" fill="none" stroke="url(#crown-gold-front)" strokeWidth="2.5" />
                                <path d="M32,44 L32,20" fill="none" stroke="url(#crown-gold-front)" strokeWidth="2" opacity="0.8" />

                                {/* 4. Front main gold frame with 3 prominent peaks */}
                                <path 
                                  d="M10,42 C10,48 54,48 54,42 L50,30 L41,36 L32,15 L23,36 L14,30 Z" 
                                  fill="url(#crown-gold-front)" 
                                  stroke="url(#crown-gold-back)" 
                                  strokeWidth="0.75" 
                                />

                                {/* 5. Front Jewels on the base band */}
                                <rect x="15" y="43.5" width="4" height="4" rx="1" transform="rotate(45 17 45.5)" fill="url(#crown-ruby)" />
                                <circle cx="24" cy="45.5" r="2.2" fill="url(#crown-pearl)" />
                                <rect x="30" y="44" width="4" height="4" rx="1" transform="rotate(45 32 46)" fill="url(#crown-ruby)" />
                                <circle cx="40" cy="45.5" r="2.2" fill="url(#crown-pearl)" />
                                <rect x="47" y="43.5" width="4" height="4" rx="1" transform="rotate(45 49 45.5)" fill="url(#crown-ruby)" />

                                {/* 6. Ornaments on top of the front peaks */}
                                <circle cx="14" cy="30" r="2.5" fill="url(#crown-pearl)" />
                                <circle cx="50" cy="30" r="2.5" fill="url(#crown-pearl)" />
                                <circle cx="32" cy="15" r="3.5" fill="url(#crown-pearl)" />
                                <path d="M32,9 L32,15 M29,12 L35,12" stroke="url(#crown-gold-front)" strokeWidth="1.5" strokeLinecap="round" />
                              </motion.svg>
                              T
                            </span>
                            {rest}
                          </>
                        );
                      }
                      return text;
                    };

                    return (
                      <section
                        key={category.id}
                        id={`category-${category.id}`}
                        className="py-12 md:py-16 px-6 md:px-12 max-w-[100rem] mx-auto border-b border-neutral-900/40 last:border-0 relative overflow-hidden"
                      >
                        {category.name?.toLowerCase().includes('monsoon') && (
                          <>
                            {/* Animated Lightning Bolts for Monsoon Category */}
                            <LightningBolts />
                            <RainEffect />
                            <motion.div
                              className="absolute top-[0%] right-[0%] w-[300px] h-[300px] md:w-[600px] md:h-[600px] bg-blue-500/20 rounded-full blur-[100px] pointer-events-none z-0"
                              animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.2, 1] }}
                              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            />
                          </>
                        )}
                        <div className="flex flex-col items-center text-center justify-center mb-8 md:mb-12 gap-6 relative z-10">
                          <div className="flex flex-col items-center">
                            <div className="flex flex-col items-center gap-3 mb-4">
                              <motion.h2
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, ease: "easeOut" }}
                                viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                                className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tighter text-center"
                              >
                                {remainingText ? (
                                  <>
                                    {renderRemainingText(remainingText)}
                                    {" "}
                                  </>
                                ) : (
                                  ""
                                )}
                                <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A] font-serif">
                                  {lastWord}
                                </span>
                              </motion.h2>
                            </div>
                            {category.description && (
                              <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                                viewport={{ once: true }}
                                className="text-sm font-light text-neutral-400 tracking-wide max-w-2xl text-center"
                              >
                                {category.description}
                              </motion.p>
                            )}
                          </div>
                        </div>

                        <div className={categoryProducts.length === 1 
                          ? "relative z-10 flex justify-center w-full pb-4 md:pb-0"
                          : "relative z-10 flex overflow-x-auto scroll-smooth overscroll-x-contain snap-x snap-mandatory hide-scrollbar gap-6 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-x-6 md:gap-y-12 pb-4 md:pb-0"
                        }>
                          {categoryProducts.map((product, index) => (
                            <div
                              key={product.id}
                              className={categoryProducts.length === 1
                                ? "group w-[75vw] md:w-[350px] max-w-full cursor-pointer"
                                : "group min-w-[65vw] snap-start md:min-w-0 md:w-auto cursor-pointer"
                              }
                              style={{ transitionDelay: `${index * 100}ms` }}
                              onClick={() => navigateToPdp(product)}
                            >
                              <div className="relative aspect-[3/4] bg-neutral-900 rounded-xl overflow-hidden mb-6">
                                <img
                                  loading={index < 2 ? "eager" : "lazy"}
                                  src={product.image || undefined}
                                  alt={product.name}
                                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                  referrerPolicy="no-referrer"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-700 flex items-center justify-center"></div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedProductId(product.id);
                                    if (product.inviteOnly) {
                                      trackEvent("click_join_waitlist", {
                                        item_name: product.name,
                                        source: `homepage_category_${category.name}`,
                                        invite_only: true,
                                      });
                                      setIsWaitlistOpen(true);
                                    } else if (
                                      product.accessMode !== "waitlist" &&
                                      product.inventory > 0 &&
                                      !product.dropDate
                                    ) {
                                      trackEcommerceEvent("begin_checkout", product, {
                                        source: `homepage_category_${category.name}`,
                                      });
                                      setIsOrderModalOpen(true);
                                    } else {
                                      trackEvent("click_join_waitlist", {
                                        item_name: product.name,
                                        source: `homepage_category_${category.name}`,
                                      });
                                      setIsWaitlistOpen(true);
                                    }
                                  }}
                                  className="absolute bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] bg-black/40 border border-white/30 backdrop-blur-md text-white py-3 text-xs font-medium tracking-widest uppercase translate-y-0 lg:translate-y-[150%] transition-all duration-500 ease-premium lg:group-hover:translate-y-0 hover:bg-white hover:text-black cursor-pointer hidden md:block"
                                >
                                  {product.inviteOnly
                                    ? "Get Invite"
                                    : product.accessMode !== "waitlist" && product.inventory > 0 && !product.dropDate
                                    ? "Buy Now"
                                    : "Join Waitlist"}
                                </button>
                              </div>
                              <ProductCardInfo
                                product={product}
                                titleClass="text-sm font-semibold tracking-tight uppercase text-white whitespace-nowrap"
                                notesClass="text-xs text-neutral-500 font-light tracking-wide truncate"
                                showInventory={true}
                              />
                            </div>
                          ))}
                        </div>
                      </section>
                    );
                  })
                ) : (
                  liveProducts.length > 0 && (
                    <section
                      id="shop-now"
                      className="py-12 md:py-16 px-6 md:px-12 max-w-[100rem] mx-auto"
                    >
                      <div className="flex flex-col items-center text-center justify-center mb-8 md:mb-12 gap-6">
                        <div className="flex flex-col items-center">
                          <div className="flex flex-col items-center gap-3 mb-4">
                            <motion.h2 
                              initial={{ opacity: 0, y: 30 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.8, ease: "easeOut" }}
                              viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                              className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tighter text-center"
                            >
                              Shop{' '}
                              <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A] font-serif">now</span>
                            </motion.h2>
                            <div className="flex items-center gap-2 bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20 mt-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                              <span className="text-[0.65rem] uppercase font-medium tracking-widest text-green-500">
                                Live
                              </span>
                            </div>
                          </div>
                          <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                            viewport={{ once: true }}
                            className="text-sm font-light text-neutral-400 tracking-wide max-w-md text-center"
                          >
                            Secure your signature scent before it's gone.
                          </motion.p>
                        </div>
                      </div>

                      <div className={liveProducts.length === 1
                        ? "flex justify-center w-full pb-4 md:pb-0"
                        : "flex overflow-x-auto scroll-smooth overscroll-x-contain snap-x snap-mandatory hide-scrollbar gap-6 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-x-6 md:gap-y-12 pb-4 md:pb-0"
                      }>
                        {liveProducts.map((product, index) => (
                          <div
                            key={product.id}
                            className={liveProducts.length === 1
                              ? "group w-[75vw] md:w-[350px] max-w-full cursor-pointer"
                              : "group min-w-[65vw] snap-start md:min-w-0 md:w-auto cursor-pointer"
                            }
                            style={{ transitionDelay: `${index * 100}ms` }}
                            onClick={() => navigateToPdp(product)}
                          >
                            <div className="relative aspect-[3/4] bg-neutral-900 rounded-xl overflow-hidden mb-6">
                              <img
                                loading={index < 2 ? "eager" : "lazy"}
                                fetchPriority={index < 2 ? "high" : "auto"}
                                src={product.image || undefined}
                                alt={product.name}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                referrerPolicy="no-referrer"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-700 flex items-center justify-center"></div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProductId(product.id);
                                  if (product.inviteOnly) {
                                    trackEvent("click_join_waitlist", {
                                      item_name: product.name,
                                      source: "homepage_collections",
                                      invite_only: true,
                                    });
                                    setIsWaitlistOpen(true);
                                  } else if (
                                    product.accessMode !== "waitlist" &&
                                    product.inventory > 0 &&
                                    !product.dropDate
                                  ) {
                                    trackEcommerceEvent("begin_checkout", product, { source: "homepage_collections" });
                                    setIsOrderModalOpen(true);
                                  } else {
                                    trackEvent("click_join_waitlist", { item_name: product.name, source: "homepage_collections" });
                                    setIsWaitlistOpen(true);
                                  }
                                }}
                                className="absolute bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] bg-black/40 border border-white/30 backdrop-blur-md text-white py-3 text-xs font-medium tracking-widest uppercase translate-y-0 lg:translate-y-[150%] transition-all duration-500 ease-premium lg:group-hover:translate-y-0 hover:bg-white hover:text-black cursor-pointer hidden md:block"
                              >
                                {product.inviteOnly
                                  ? "Get Invite"
                                  : product.accessMode !== "waitlist" && product.inventory > 0 && !product.dropDate
                                  ? "Buy Now"
                                  : "Join Waitlist"}
                              </button>
                            </div>
                            <ProductCardInfo 
                              product={product} 
                              titleClass="text-sm font-semibold tracking-tight uppercase text-white whitespace-nowrap"
                              notesClass="text-xs text-neutral-500 font-light tracking-wide truncate"
                              showInventory={true}
                            />
                          </div>
                        ))}
                      </div>
                    </section>
                  )
                )
              )}



              <DesktopSequence 
                ctaTextDesktop={desktopCarouselItem?.ctaText}
                ctaTextMobile={mobileCarouselItem?.ctaText}
                shouldLoadVideo={mobileVideoReady || desktopVideoReady || mobileVideoError !== null || desktopVideoError !== null}
                onCtaClick={() => scrollToSeasonalDrops("made_for_elite_experience_now")}
              />

              {/* The Waitlist Section */}
              {false && (
                <div className="bg-[#0B0B0C] text-white py-12 md:py-16 px-6 md:px-12 text-center overflow-hidden relative transform-gpu">
                  {/* Static Center Glow */}
                  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden opacity-40">
                    <div 
                      className="absolute top-1/2 left-1/2 w-[180vw] h-[180vw] md:w-[900px] md:h-[900px] rounded-full -translate-x-1/2 -translate-y-1/2"
                      style={{ background: 'radial-gradient(circle, rgba(223,179,122,0.15) 0%, rgba(223,179,122,0) 60%)' }}
                    />
                  </div>
    
                  {/* Dynamic Animated Radial Glows */}
                  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden opacity-100 md:opacity-80">
                    {/* Glow 1 */}
                    <motion.div 
                      className="absolute w-[120vw] h-[120vw] md:w-[600px] md:h-[600px] rounded-full -translate-x-1/2 -translate-y-1/2"
                      style={{ background: 'radial-gradient(circle, rgba(223,179,122,0.3) 0%, rgba(223,179,122,0) 40%)' }}
                      animate={{
                        top: ["20%", "40%", "80%", "30%"],
                        left: ["30%", "70%", "20%", "50%"],
                        opacity: [0.1, 0.5, 0.1, 0.1],
                        scale: [0.8, 1.2, 0.8, 1]
                      }}
                      transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                    />
                    {/* Glow 2 */}
                    <motion.div 
                      className="absolute w-[120vw] h-[120vw] md:w-[600px] md:h-[600px] rounded-full -translate-x-1/2 -translate-y-1/2"
                      style={{ background: 'radial-gradient(circle, rgba(223,179,122,0.3) 0%, rgba(223,179,122,0) 40%)' }}
                      animate={{
                        top: ["80%", "20%", "60%", "40%"],
                        left: ["80%", "20%", "60%", "80%"],
                        opacity: [0.1, 0.1, 0.5, 0.1],
                        scale: [1, 0.8, 1.2, 1]
                      }}
                      transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
                    />
                    {/* Glow 3 */}
                    <motion.div 
                      className="absolute w-[120vw] h-[120vw] md:w-[600px] md:h-[600px] rounded-full -translate-x-1/2 -translate-y-1/2"
                      style={{ background: 'radial-gradient(circle, rgba(223,179,122,0.3) 0%, rgba(223,179,122,0) 40%)' }}
                      animate={{
                        top: ["50%", "70%", "30%", "80%"],
                        left: ["20%", "80%", "50%", "30%"],
                        opacity: [0.5, 0.1, 0.1, 0.5],
                        scale: [1.2, 0.8, 1, 1.2]
                      }}
                      transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    />
                  </div>
    
                  <div className="max-w-2xl mx-auto space-y-8 relative z-10 px-4">
                    <motion.h2 
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                      className="text-4xl md:text-7xl font-semibold leading-[1.05] tracking-tighter"
                    >
                      The waitlist is <br />
                      <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A] font-serif">already growing</span>
                    </motion.h2>
    
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                      viewport={{ once: true }}
                      className="max-w-md mx-auto text-sm md:text-base text-neutral-400 font-light leading-relaxed space-y-4"
                    >
                      <p>
                        Monsoon 26 officially launches on <strong className="text-white">June 20, 2026</strong>. Kayoki releases historically sell out in mere minutes.
                      </p>
                      <p>
                        Join the waitlist to secure priority early access, giving you the best chance to acquire a bottle before the public release.
                      </p>
                    </motion.div>
    
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: 0.4, ease: "backOut" }}
                      viewport={{ once: true }}
                      className="pt-6"
                    >
                      <button 
                        onClick={() => {
                            trackEvent("click_join_waitlist", { source: "home_waitlist_section_hero_btn" });
                            if (upcomingProducts.length > 0) {
                              setSelectedProductId(upcomingProducts[0].id);
                            }
                            setIsWaitlistOpen(true);
                        }}
                        className="border-b border-[#DFB37A] text-[#DFB37A] pb-1 text-sm md:text-base font-light tracking-wider hover:opacity-80 transition-all duration-300 md:hover:scale-[1.02] md:active:scale-[0.98] inline-block cursor-pointer uppercase"
                      >
                        Join Waitlist
                      </button>
                    </motion.div>
                  </div>
                </div>
              )}

              {/* Philosophy Section */}
              <section className="bg-black py-12 md:py-16 overflow-hidden">
                {/* Exact Desktop Layout (visible on xl and up to prevent horizontal overflow constraints) */}
                <div className="hidden xl:grid grid-cols-[1fr_auto_1fr] gap-4 2xl:gap-6 items-center min-h-[600px] 2xl:min-h-[700px] max-w-[1440px] mx-auto px-4 reveal-on-scroll">
                  <div className="relative h-[550px] 2xl:h-[650px] w-full min-w-0">
                    <div className="absolute top-0 left-0 w-[260px] 2xl:w-[340px] h-[340px] 2xl:h-[432px] z-10 transition-transform hover:scale-[1.02] duration-700 stagger-up delay-100">
                      <div className="w-full h-full anim-float-1">
                        <img
                          alt="Man with watch"
                          className="w-full h-full object-cover"
                          loading="lazy"
                          src="https://lh3.googleusercontent.com/d/1Wg0ptTbyOOni3gRfcuub6N6ovX9FMJbS"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    </div>
                    <div className="absolute top-0 left-[180px] 2xl:left-[240px] w-[200px] 2xl:w-[261px] h-[190px] 2xl:h-[248px] z-[8] stagger-up delay-200">
                      <div className="w-full h-full anim-float-2">
                        <img
                          alt="Aim illustration"
                          className="w-full h-full object-contain opacity-80"
                          loading="lazy"
                          src="https://lh3.googleusercontent.com/d/1i5J0TFjZxQCnBP9nJbp-ieELFkBkonOu"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    </div>
                    <div className="absolute bottom-[10px] left-0 w-[140px] 2xl:w-[180px] h-[180px] 2xl:h-[240px] z-[8] stagger-up delay-300">
                      <div className="w-full h-full anim-float-3">
                        <img
                          alt="Strategy sketch"
                          className="w-full h-full object-contain opacity-80"
                          loading="lazy"
                          src="https://lh3.googleusercontent.com/d/1NSKXwtHfkmbSc3IozFxgT3TBtgO9wBJh"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    </div>
                    <div className="absolute bottom-[10px] left-[160px] 2xl:left-[220px] w-[180px] 2xl:w-[230px] h-[220px] 2xl:h-[280px] z-20 transition-transform hover:scale-[1.02] duration-700 stagger-up delay-400">
                      <div className="w-full h-full anim-float-4">
                        <img
                          alt="Gold watch close-up"
                          className="w-full h-full object-cover"
                          loading="lazy"
                          src="https://lh3.googleusercontent.com/d/1gNSMJyI6HM-omFMfls130fT-hHDB_gkL"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="z-40 flex flex-col items-center justify-center text-center max-w-[420px] 2xl:max-w-[490px] px-2 2xl:px-6">
                    <motion.h2 
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                      className="text-4xl xl:text-5xl 2xl:text-6xl font-semibold leading-[1.05] tracking-tighter mb-4 2xl:mb-6 text-white text-center"
                    >
                      Handmade Luxury, <br className="md:hidden" />
                      <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A] font-serif">Uncompromised</span>
                    </motion.h2>
                    <p className="text-[#999] text-[14px] 2xl:text-[16px] font-light leading-[160%] 2xl:leading-[170%] tracking-normal mb-3 2xl:mb-4 max-w-[360px] 2xl:max-w-[420px]">
                      Each Kayoki fragrance is blended and refined entirely by hand by master artisans with over 30 years of perfumery expertise.
                    </p>
                    <p className="text-[#999] text-[14px] 2xl:text-[16px] font-light leading-[160%] 2xl:leading-[170%] tracking-normal mb-3 2xl:mb-4 max-w-[360px] 2xl:max-w-[420px]">
                      Free from mass-production machinery, every bottle is created through a meticulous artisanal process designed to preserve the purity, balance, and richness of the fragrance.
                    </p>
                    <p className="text-white/80 text-[13px] 2xl:text-[14px] font-light leading-[160%] 2xl:leading-[170%] tracking-normal max-w-[360px] 2xl:max-w-[420px]">
                      A slower process. A finer result.
                    </p>
                  </div>

                  <div className="relative h-[550px] 2xl:h-[650px] w-full min-w-0">
                    <div className="absolute top-0 right-0 w-[240px] 2xl:w-[300px] h-[320px] 2xl:h-[400px] z-10 transition-transform hover:scale-[1.02] duration-700 stagger-up delay-100">
                      <div className="w-full h-full anim-float-1">
                        <img
                          alt="Watch mechanism machining"
                          className="w-full h-full object-cover"
                          loading="lazy"
                          src="https://lh3.googleusercontent.com/d/1C6g76ev_PhNAQt6AT_sYF8EWNuRFpO8z"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    </div>
                    <div className="absolute top-[220px] 2xl:top-[280px] right-[160px] 2xl:right-[220px] w-[180px] 2xl:w-[240px] h-[240px] 2xl:h-[320px] z-20 transition-transform hover:scale-[1.02] duration-700 stagger-up delay-200">
                      <div className="w-full h-full anim-float-2">
                        <img
                          alt="Watch detail"
                          className="w-full h-full object-cover"
                          loading="lazy"
                          src="https://lh3.googleusercontent.com/d/1p_3rHXSBXNe_nmPZfbbOEJUUNH3zv9gG"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    </div>
                    <div className="absolute bottom-0 right-[30px] 2xl:right-[40px] w-[180px] 2xl:w-[237px] h-[220px] 2xl:h-[281px] z-[5] stagger-up delay-300">
                      <div className="w-full h-full anim-float-3">
                        <img
                          alt="Horse sketch"
                          className="w-full h-full object-contain opacity-80"
                          loading="lazy"
                          src="https://lh3.googleusercontent.com/d/1NhxjzuVWcDmh0H2pizueA1NT2gCq4aK7"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile/Tablet Exact Layout */}
                <div className="xl:hidden relative overflow-hidden px-4 reveal-on-scroll">
                  <div className="flex flex-col items-center text-center">
                    <motion.h2 
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                      className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tighter mb-5 text-white text-center"
                    >
                      Handmade Luxury, <br className="md:hidden" />
                      <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A] font-serif">Uncompromised</span>
                    </motion.h2>
                    <p className="text-[#999999] text-[15px] font-light leading-[170%] mb-4 max-w-[320px]">
                      Each Kayoki fragrance is blended and refined entirely by hand by master artisans with over 30 years of perfumery expertise.
                    </p>
                    <p className="text-[#999999] text-[15px] font-light leading-[170%] mb-4 max-w-[320px]">
                      Free from mass-production machinery, every bottle is created through a meticulous artisanal process designed to preserve the purity, balance, and richness of the fragrance.
                    </p>
                    <p className="text-white/80 text-[13px] font-light leading-[170%] max-w-[320px]">
                      A slower process. A finer result.
                    </p>
                    <div className="relative w-full max-w-[400px] h-[380px] mt-6 overflow-hidden">
                      <div className="absolute left-0 top-[160px] w-[140px] h-[220px] z-10 anim-float-1">
                        <img
                          alt="Kayoki Product 1"
                          className="w-full h-full object-cover rounded-sm"
                          loading="lazy"
                          src="https://lh3.googleusercontent.com/d/1Wg0ptTbyOOni3gRfcuub6N6ovX9FMJbS"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                      <div className="absolute right-0 top-0 w-[75%] h-[350px] anim-float-2">
                        <img
                          alt="Kayoki Product 2"
                          className="w-full h-full object-cover rounded-sm"
                          loading="lazy"
                          src="https://lh3.googleusercontent.com/d/1C6g76ev_PhNAQt6AT_sYF8EWNuRFpO8z"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Behind the Craft: Packing Table Section */}
              <PackingTableSection />

              {/* Our Collections Section */}
              {isLoadingCollections ? (
                <section className="pt-16 pb-12 md:pt-24 md:pb-16 px-6 md:px-12 max-w-[100rem] mx-auto">
                  <div className="flex flex-col mb-8 md:mb-12">
                    <div className="h-8 bg-neutral-800 rounded w-48 mb-4 animate-pulse"></div>
                    <div className="h-4 bg-neutral-800 rounded w-full max-w-sm animate-pulse"></div>
                  </div>
                </section>
              ) : (
                collectionsItems.length > 0 && (
                  <section className="pt-16 pb-12 md:pt-24 md:pb-16 max-w-[100rem] mx-auto overflow-hidden">
                    <div className="flex flex-col items-center text-center justify-center mb-8 md:mb-12 px-6 md:px-12">
                      <div className="flex flex-col items-center">
                        <motion.h2 
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
                          className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tighter mb-4 text-center"
                        >
                          Our{' '}
                          <span className="italic font-light text-transparent bg-clip-text bg-gradient-to-r from-[#DFB37A] via-[#E5CCAA] to-[#DFB37A] font-serif">collections</span>
                        </motion.h2>
                        <motion.p 
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                          viewport={{ once: true }}
                          className="text-sm font-light text-neutral-400 tracking-wide text-center"
                        >
                          Explore all the limited edition collections of Kayoki
                        </motion.p>
                      </div>
                    </div>

                    <div className="relative w-full">
                      <div
                        ref={collectionsScrollRef}
                        className={`flex overflow-x-auto hide-scrollbar w-full relative group ${isDragging ? "cursor-grabbing select-none" : "cursor-grab"}`}
                        onMouseEnter={() => setIsAutoScrolling(false)}
                        onMouseLeave={() => {
                          setIsAutoScrolling(true);
                          handleMouseUp();
                        }}
                        onTouchStart={() => setIsAutoScrolling(false)}
                        onTouchEnd={() => {
                          setTimeout(() => setIsAutoScrolling(true), 1500);
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onMouseMove={handleMouseMove}
                      >
                        <div className="w-6 md:w-12 flex-shrink-0" />

                        <div
                          className="flex gap-6 flex-shrink-0"
                          ref={firstSetRef}
                        >
                          {collectionsItems.map((item, index) => (
                            <div
                              key={`collection1-${index}`}
                              className="flex-shrink-0 w-[70vw] md:w-[350px] aspect-[4/5] rounded-xl overflow-hidden bg-neutral-900 pointer-events-none"
                            >
                              <img
                                loading="lazy"
                                src={item.image || undefined}
                                alt={item.name}
                                className="w-full h-full object-cover transition-transform duration-[2s] ease-premium group-hover:scale-105"
                                referrerPolicy="no-referrer"
                              />
                            </div>
                          ))}
                        </div>

                        {collectionsItems.length > 4 && (
                          <div
                            className="flex gap-6 flex-shrink-0 ml-6"
                            ref={secondSetRef}
                          >
                            {collectionsItems.map((item, index) => (
                              <div
                                key={`collection2-${index}`}
                                className="flex-shrink-0 w-[70vw] md:w-[350px] aspect-[4/5] rounded-xl overflow-hidden bg-neutral-900 pointer-events-none"
                              >
                                <img
                                  loading="lazy"
                                  src={item.image || undefined}
                                  alt={item.name}
                                  className="w-full h-full object-cover transition-transform duration-[2s] ease-premium group-hover:scale-105"
                                  referrerPolicy="no-referrer"
                                />
                              </div>
                            ))}
                          </div>
                        )}

                        {collectionsItems.length > 4 ? (
                          <div className="w-6 md:w-12 flex-shrink-0 ml-6" />
                        ) : (
                          <div className="w-6 md:w-12 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </section>
                )
              )}




            </>
          </motion.div>
        )}
      </AnimatePresence>
      </main>

      {/* Write the Modals down here */}
      <AnimatePresence>
        {showPromoPopup && (
          <div className="fixed inset-0 flex items-center justify-center z-[110] p-4 font-sans">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-md cursor-pointer"
              onClick={() => { trackEvent("abandon_promo"); setShowPromoPopup(false); }}
            ></motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-black border border-white/10 max-w-[420px] w-full overflow-hidden shadow-2xl rounded-xl"
            >
              <button
                onClick={() => { trackEvent("abandon_promo"); setShowPromoPopup(false); }}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-md text-white/70 hover:text-white hover:bg-black/80 transition-colors z-20 cursor-pointer"
                aria-label="Close modal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <div className="relative h-[240px] w-full bg-neutral-900 border-b border-white/10">
                <img
                  loading="lazy"
                  src="https://lh3.googleusercontent.com/d/1CTfppBA9CeUpEfaeNpHPmvbH_8RssODj"
                  alt="Kayoki Summer Collection"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
              </div>
              <div className="p-8 md:p-10 flex flex-col items-center text-center">
                <h2 className="text-[1.1rem] md:text-xl font-bold tracking-[0.15em] uppercase text-white mb-2 leading-tight">
                  SUMMER COLLECTION <br />
                  <span className="text-amber-500">20th JUNE 2026</span>
                </h2>
                <h3 className="text-sm font-semibold tracking-widest uppercase text-neutral-300 mb-4">
                  Join the Waitlist Now!
                </h3>
                <p className="text-xs text-neutral-400 font-light tracking-wide leading-relaxed mb-8">
                  Get in the waitlist to get access to buy the summer collection
                  on the launch day.
                </p>
                <button
                  onClick={() => {
                    trackEvent("click_join_waitlist", { source: "promo_popup_cta" });
                    setShowPromoPopup(false);
                    setIsWaitlistOpen(true);
                  }}
                  className="w-full bg-white text-black py-4 text-xs font-bold tracking-[0.2em] uppercase hover:bg-neutral-200 transition-all duration-300 md:hover:scale-[1.02] md:active:scale-[0.98] rounded-none cursor-pointer"
                >
                  Join Waitlist
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeModal && (
          <div className="fixed inset-0 flex items-center justify-center z-[100]">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-md cursor-pointer"
              onClick={() => setActiveModal(null)}
            ></motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-white rounded-2xl max-w-[460px] w-full mx-4 overflow-hidden shadow-2xl"
            >
              <div className="flex items-center justify-between p-4 pb-4 px-6">
                <h2 className="text-[#1A1A1A] text-[24px] font-sans font-semibold capitalize">
                  {activeModal.title}
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className="absolute top-4 right-4 w-6 h-6 flex items-center justify-center rounded-full bg-[#F5F5F5] hover:bg-[#E5E5E5] transition-colors cursor-pointer"
                  aria-label="Close modal"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M15 5L5 15M5 5L15 15"
                      stroke="#1A1A1A"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                </button>
              </div>
              <div className="px-0">
                <div className="overflow-hidden">
                  <img
                    loading="lazy"
                    alt={activeModal.title}
                    className="w-full object-cover"
                    src={activeModal.img || undefined}
                    style={{ height: "280px", maxWidth: "none" }}
                  />
                </div>
              </div>
              <div className="p-6 pt-5">
                <p className="text-[#1A1A1A] text-[16px] font-sans font-normal leading-[170%] text-justify">
                  {activeModal.desc}
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isWaitlistOpen && (
          <div
            className="fixed inset-0 flex justify-center items-end md:items-center p-0 md:p-6 z-[100]"
            style={{ overscrollBehavior: "none" }}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-md cursor-pointer block"
              onClick={closeWaitlist}
              style={{ touchAction: "none" }}
            ></motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 150 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 150 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              data-scroll-container="true"
              className="w-full md:max-w-[400px] rounded-t-2xl md:rounded-xl max-h-[90vh] bg-white shadow-2xl flex flex-col z-10 overflow-hidden mb-0"
            >
              <style
                dangerouslySetInnerHTML={{
                  __html: `
              .waitlist-scroll::-webkit-scrollbar {
                width: 6px;
                background-color: #ffffff;
              }
              .waitlist-scroll::-webkit-scrollbar-track {
                background-color: #ffffff;
              }
              .waitlist-scroll::-webkit-scrollbar-thumb {
                background-color: #d1d5db;
                border-radius: 10px;
              }
              .waitlist-scroll:hover::-webkit-scrollbar-thumb,
              .waitlist-scroll:focus-within::-webkit-scrollbar-thumb {
                background-color: #9ca3af;
              }
            `,
                }}
              />

              {/* Sticky Header Portion */}
              {!showOtpInput && !isWaitlistSuccess ? (
                <div className="p-5 pb-0 relative z-20 bg-white shrink-0 md:rounded-t-xl min-h-[48px]">
                  <button
                    onClick={closeWaitlist}
                    className="absolute top-4 right-5 w-7 h-7 rounded-full bg-[#F0F0F0] flex items-center justify-center hover:bg-[#E0E0E0] transition-colors cursor-pointer"
                    aria-label="Close"
                  >
                    <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                      <path d="M1 1L13 13M1 13L13 1" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                   <div className="text-[22px] font-sans font-semibold text-[#1A1A1A] mb-5 pr-10 mt-2">
                    <span className="font-[600]">
                      {selectedProduct && selectedProduct.inviteOnly ? "Request Invite" : "Join the Waitlist"}
                    </span>
                    <div className="font-[300] text-[13px] md:text-[14px] mt-1 text-gray-500 whitespace-nowrap truncate">
                      {selectedProduct && selectedProduct.inviteOnly
                        ? "Apply for exclusive access to this release."
                        : "Get Access to Buy Fragrances on Launch Day"}
                    </div>
                  </div>
                </div>
              ) : isWaitlistSuccess ? (
                <div className="p-5 pb-0 relative z-20 bg-white md:rounded-t-xl shrink-0 min-h-[48px]">
                  <button
                    onClick={closeWaitlist}
                    className="absolute top-4 right-5 w-7 h-7 rounded-full bg-[#F0F0F0] flex items-center justify-center hover:bg-[#E0E0E0] transition-colors cursor-pointer"
                    aria-label="Close"
                  >
                    <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M1 1L13 13M1 13L13 1"
                        stroke="#1A1A1A"
                        strokeWidth="2"
                        strokeLinecap="round"
                      ></path>
                    </svg>
                  </button>
                </div>
              ) : null}

              {/* Scrollable Form Portion */}
              <div
                className={`overflow-y-auto waitlist-scroll flex-1 relative flex flex-col ${isWaitlistSuccess ? "pt-2" : showOtpInput ? "p-0" : "p-5 pt-0 pb-[calc(env(safe-area-inset-bottom)+1.25rem)]"}`}
              >
                {isWaitlistSuccess ? (
                  <div className="flex flex-col items-center justify-center text-center flex-1">
                    <div className="w-24 h-24 mb-6 mt-6 relative flex items-center justify-center">
                      <svg
                        width="96"
                        height="96"
                        viewBox="0 0 64 64"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 28L32 12L52 28H12Z"
                          stroke="#1A1A1A"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                          fill="white"
                        />
                        <path
                          d="M16 26 L18 23 L20 26 L22 23 L24 26 L26 23 L28 26 L30 23 L32 26 L34 23 L36 26 L38 23 L40 26 L42 23 L44 26 L46 23 L48 26 L48 40 L16 40 Z"
                          fill="#3D6E5D"
                        />
                        <path
                          d="M26 33L30 37L39 28"
                          stroke="white"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 28V48H52V28L32 40L12 28Z"
                          stroke="#1A1A1A"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                          fill="white"
                        />
                        <path
                          d="M12 48L25.2 38"
                          stroke="#1A1A1A"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M52 48L38.8 38"
                          stroke="#1A1A1A"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <h3 className="text-[32px] md:text-[36px] font-bold text-[#1A1A1A] leading-[1.1] tracking-tight mb-4">
                      {selectedProduct && selectedProduct.inviteOnly ? (
                        <>
                          Your request
                          <br className="md:hidden" />
                          is in
                        </>
                      ) : (
                        <>
                          You're
                          <br className="md:hidden" />
                          officially in.
                        </>
                      )}
                    </h3>
                    {selectedProduct && selectedProduct.inviteOnly ? (
                      <>
                        <p className="text-[16px] md:text-[18px] text-[#1A1A1A] font-medium leading-[1.4] mb-3 px-4 max-w-sm">
                          We’ve noted your interest in <span className="font-semibold">{selectedProduct.name}</span>
                        </p>
                        <p className="text-[14px] text-gray-500 leading-[1.5] mb-8 px-6 max-w-xs">
                          Invitations open in small numbers. If one becomes available for you, we’ll reach out to you!
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-[18px] md:text-[20px] font-bold text-[#1A1A1A] mb-2">
                          #{waitlistPosition.toLocaleString()} on the waitlist
                        </p>
                        <p className="text-[14px] text-gray-500 mb-8">
                          Application submitted successfully
                        </p>
                      </>
                    )}

                    <div className="w-full bg-[#3D6E5D] text-white pt-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] px-4 text-[15px] md:text-[16px] font-medium mt-auto text-center mt-8">
                      {selectedProduct && selectedProduct.inviteOnly ? (
                        `Reference #${waitlistPosition.toLocaleString()}`
                      ) : (
                        <>
                          {totalPieces.toLocaleString()} pieces.{" "}
                          {[
                            "Zero",
                            "One",
                            "Two",
                            "Three",
                            "Four",
                            "Five",
                            "Six",
                            "Seven",
                            "Eight",
                            "Nine",
                            "Ten",
                          ][collectionCount] || collectionCount}{" "}
                          fragrances.
                        </>
                      )}
                    </div>
                  </div>
                ) : showOtpInput ? (
                  <div className="p-5 pt-4 animate-in fade-in zoom-in-95 duration-300 relative bg-white">
                    <div className="relative">
                      <button
                        onClick={closeWaitlist}
                        className="absolute top-0 right-0 w-7 h-7 rounded-full bg-[#F0F0F0] flex items-center justify-center hover:bg-[#E0E0E0] transition-colors cursor-pointer z-10"
                        aria-label="Close"
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M1 1L13 13M1 13L13 1"
                            stroke="#1A1A1A"
                            strokeWidth="2"
                            strokeLinecap="round"
                          ></path>
                        </svg>
                      </button>
                      <div className="flex flex-col py-4">
                        <h2 className="text-[22px] font-sans font-semibold text-[#1A1A1A] mb-2 pr-10">
                          Verify your phone
                        </h2>
                        <p className="text-[14px] text-gray-500 font-sans mb-6">
                          We sent a 6-digit code to +91{waitlistPhone}
                        </p>
                        <form
                          className="space-y-4"
                          onSubmit={handleWaitlistSubmit}
                        >
                          <div>
                            <label className="text-[14px] text-[#1A1A1A] font-sans mb-1 block">
                              OTP <span className="text-[#E91E63]">*</span>
                            </label>
                            <input
                              inputMode="numeric"
                              maxLength={6}
                              placeholder="Enter 6-digit OTP"
                              className="w-full bg-transparent text-[#1A1A1A] placeholder:text-gray-400 text-[16px] font-sans focus:outline-none border-b-[2px] pb-3 border-[#E4E4E7]"
                              type="text"
                              required
                              value={otp}
                              onChange={(e) =>
                                setOtp(
                                  e.target.value.replace(/\D/g, "").slice(0, 6),
                                )
                              }
                            />
                          </div>
                          {otpError && (
                            <div className="bg-red-50 text-[#E91E63] text-[13px] p-2 rounded-md mb-2 text-center border border-red-100 animate-in fade-in">
                              {otpError}
                            </div>
                          )}
                          <button
                            type="submit"
                            disabled={isSubmittingWaitlist || otp.length < 6}
                            className="w-full h-[50px] bg-[#1A1A1A] text-white font-sans font-semibold text-[15px] hover:bg-[#333] transition-all duration-300 md:hover:scale-[1.02] md:active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                          >
                            {isSubmittingWaitlist
                              ? "Verifying..."
                              : "Verify & Submit"}
                          </button>
                          <div className="flex items-center justify-between text-[12px]">
                            <button
                              type="button"
                              onClick={() => { setShowOtpInput(false); cleanupRecaptcha(); }}
                              className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                            >
                              Change phone
                            </button>
                            <button
                              type="button"
                              disabled={resendTimer > 0}
                              onClick={handleResendOtp}
                              className="text-[#1A1A1A] hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {resendTimer > 0
                                ? `Resend in ${resendTimer}s`
                                : "Resend"}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full">
                    <div className="relative mb-6">
                      {(() => {
                        const isInviteOnly = selectedProduct && !!selectedProduct.inviteOnly;
                        const optionsList = isInviteOnly
                          ? products.filter((p) => p.inviteOnly)
                          : upcomingProducts;
                        const hasMultipleOptions = optionsList.length > 1;

                        return (
                          <>
                            <div
                              className={`flex items-center gap-3 p-3 bg-[#060606] rounded-[8px] ${
                                hasMultipleOptions ? "cursor-pointer" : "cursor-default"
                              }`}
                              onClick={hasMultipleOptions ? () => setIsDropdownOpen(!isDropdownOpen) : undefined}
                            >
                              <div className="relative w-[60px] h-[60px] shrink-0 overflow-hidden bg-white">
                                <img
                                  alt={selectedProduct.name}
                                  loading="lazy"
                                  className="object-cover scale-[1.6]"
                                  src={selectedProduct.image || undefined}
                                  style={{
                                    position: "absolute",
                                    height: "100%",
                                    width: "100%",
                                    inset: 0,
                                  }}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-[16px] text-[#FFFFFF]">
                                  <span className="font-[600]">
                                    {selectedProduct.name}
                                  </span>{" "}
                                </h4>
                                <p className="text-[12px] font-[300] text-[#CCCCCC] mt-0.5">
                                  {selectedProduct.notes}
                                </p>
                              </div>
                              {hasMultipleOptions && (
                                <svg
                                  className={`shrink-0 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M10 13.75L3.75 7.5L4.625 6.625L10 12L15.375 6.625L16.25 7.5L10 13.75Z"
                                    fill="white"
                                  ></path>
                                </svg>
                              )}
                            </div>
                            {isDropdownOpen && hasMultipleOptions && (
                              <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a1a] border border-white/10 rounded-[8px] overflow-hidden z-20 max-h-[200px] overflow-y-auto">
                                {optionsList.map((p) => (
                                  <div
                                    key={p.id}
                                    className="flex items-center gap-3 p-3 hover:bg-[#333] cursor-pointer transition-colors"
                                    onClick={() => {
                                      setSelectedProductId(p.id);
                                      setIsDropdownOpen(false);
                                    }}
                                  >
                                    <div className="relative w-[40px] h-[40px] shrink-0 overflow-hidden bg-white rounded">
                                      <img
                                        alt={p.name}
                                        loading="lazy"
                                        className="object-cover scale-[1.6]"
                                        src={p.image || undefined}
                                        style={{
                                          position: "absolute",
                                          height: "100%",
                                          width: "100%",
                                          inset: 0,
                                        }}
                                      />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-[14px] text-[#FFFFFF]">
                                        <span className="font-[600]">{p.name}</span>
                                      </h4>
                                      <p className="text-[11px] font-[300] text-[#CCCCCC] mt-0.5">
                                        {p.notes}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <form className="space-y-5" onSubmit={handleWaitlistSubmit}>
                      <div>
                        <label className="text-[14px] text-[#1A1A1A] font-sans mb-1 block">
                          Full name <span className="text-[#E91E63]">*</span>
                        </label>
                        <input
                          value={waitlistName}
                          onChange={(e) => setWaitlistName(e.target.value)}
                          placeholder="Enter full name"
                          aria-label="Full name"
                          className="w-full bg-transparent text-[#1A1A1A] placeholder:text-gray-400 text-[16px] font-sans focus:outline-none border-b-[2px] pb-3 border-[#E4E4E7]"
                          type="text"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-[14px] text-[#1A1A1A] font-sans mb-1 block">
                          Email <span className="text-[#E91E63]">*</span>
                        </label>
                        <input
                          value={waitlistEmail}
                          onChange={(e) => setWaitlistEmail(e.target.value)}
                          placeholder="Enter email address"
                          aria-label="Email address"
                          className="w-full bg-transparent text-[#1A1A1A] placeholder:text-gray-400 text-[16px] font-sans focus:outline-none border-b-[2px] pb-3 border-[#E4E4E7]"
                          type="email"
                          required
                        />
                      </div>
                      <div className="sticky bottom-0 z-10 bg-white pt-2 pb-2">
                        <label className="text-[14px] text-[#1A1A1A] font-sans mb-1 block">
                          Phone <span className="text-[#E91E63]">*</span>
                        </label>
                        <div className="w-full">
                          <div className="flex items-center border-b-[2px] border-[#E4E4E7] focus-within:border-[#1A1A1A] transition-colors pb-3">
                            <div className="relative pr-2 border-r border-[#E4E4E7] mr-2 flex items-center h-full">
                              <div className="flex items-center justify-center gap-1 text-[14px] text-[#666] leading-none py-1">
                                <span className="text-[18px] leading-none mb-[2px]">
                                  🇮🇳
                                </span>
                                <span className="leading-none mt-[2px]">
                                  +91
                                </span>
                              </div>
                            </div>
                            <input
                              inputMode="numeric"
                              placeholder="Enter phone number"
                              aria-label="Phone number"
                              className="flex-1 px-1 py-1 text-[16px] bg-transparent outline-none text-[#1A1A1A] placeholder:text-gray-400"
                              type="tel"
                              required
                              maxLength={10}
                              pattern="[0-9]{10}"
                              value={waitlistPhone}
                              onChange={(e) => setWaitlistPhone(e.target.value)}
                              onInput={(e) => {
                                e.currentTarget.value = e.currentTarget.value
                                  .replace(/[^0-9]/g, "")
                                  .slice(0, 10);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      {waitlistError && (
                        <div className="bg-red-50 text-[#E91E63] text-sm p-3 rounded-md mb-2 text-center border border-red-100">
                          {waitlistError}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmittingWaitlist}
                        className="w-full h-[50px] bg-black text-white font-sans font-semibold text-[15px] hover:bg-[#333] transition-all duration-300 md:hover:scale-[1.02] md:active:scale-[0.98] rounded-full disabled:bg-gray-400 cursor-pointer"
                      >
                        {isSubmittingWaitlist
                          ? "Sending OTP..."
                          : selectedProduct && selectedProduct.inviteOnly
                          ? "Request Invite"
                          : "Join"}
                      </button>
                      <p className="text-[12px] text-gray-400 font-sans text-center leading-[1.9]">
                        By submitting, you agree to our{" "}
                        <button
                          type="button"
                          onClick={() => {
                            setIsDropdownOpen(false); // Make sure dropdown closes if any
                            setIsWaitlistOpen(false);
                            window.history.pushState({}, "", "/privacy");
                            setCurrentPath("/privacy");
                          }}
                          className="underline text-[#DFB37A] hover:text-[#C89B62] cursor-pointer"
                        >
                          Privacy Policy
                        </button>{" "}
                        and{" "}
                        <button
                          type="button"
                          onClick={() => {
                            setIsDropdownOpen(false);
                            setIsWaitlistOpen(false);
                            window.history.pushState({}, "", "/terms");
                            setCurrentPath("/terms");
                          }}
                          className="underline text-[#DFB37A] hover:text-[#C89B62] cursor-pointer"
                        >
                          Terms & Conditions
                        </button>
                        .
                      </p>
                    </form>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Newsletter */}
      {!viewingPdpProduct && currentPath !== "/admin_access" && (
        <section className="py-12 md:py-16 px-6 bg-neutral-900 relative overflow-hidden">
          <div className="relative z-10 max-w-xl mx-auto text-center">
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
              className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tighter mb-4 text-white"
            >
              Subscribe to <br className="md:hidden" />
              newsletter
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true }}
              className="text-sm font-light text-neutral-400 mb-10"
            >
              Our drops sell out in minutes. Subscribe up to receive
              regular launch updates.
            </motion.p>

            <form
              onSubmit={handleSubscribe}
              className="flex flex-col sm:flex-row gap-4 w-full relative"
            >
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="FULL NAME"
                aria-label="Full Name"
                className="flex-1 bg-transparent border border-white/20 focus:border-white transition-colors px-6 py-4 text-xs font-medium tracking-widest uppercase text-white placeholder-neutral-600 focus:outline-none w-full"
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="EMAIL ADDRESS"
                aria-label="Email Address"
                className="flex-1 bg-transparent border border-white/20 focus:border-white transition-colors px-6 py-4 text-xs font-medium tracking-widest uppercase text-white placeholder-neutral-600 focus:outline-none w-full"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-white text-black border border-white px-10 py-4 text-xs font-semibold tracking-widest uppercase hover:bg-neutral-200 transition-all duration-300 md:hover:scale-[1.02] md:active:scale-[0.98] sm:w-auto w-full shrink-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSubmitting
                  ? "Joining..."
                  : subscribed
                    ? "Joined"
                    : "Join"}
              </button>
              {subscribed && (
                <p className="absolute -bottom-8 left-0 w-full text-xs tracking-widest uppercase text-amber-500 font-medium">
                  You have been added to the list.
                </p>
              )}
            </form>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className={`bg-black pt-16 md:pt-20 px-6 md:px-12 text-neutral-400 ${viewingPdpProduct ? 'pb-[calc(110px+env(safe-area-inset-bottom))] md:pb-8' : 'pb-8'}`}>
        <div className="max-w-[100rem] mx-auto flex flex-col items-center mb-10 md:mb-12">
          <a
            href="#"
            aria-label="Kayoki Home"
            className="flex items-center justify-center mb-6 cursor-pointer"
          >
            <img
              loading="lazy"
              src="https://lh3.googleusercontent.com/d/1TCdcvg9wbkIdRRDTR_99CO6gFEYvKHxR"
              alt="Kayoki Logo"
              width="200"
              height="40"
              className="h-8 md:h-10 w-auto object-contain filter grayscale opacity-50 hover:opacity-100 transition-opacity duration-500 cursor-pointer"
              referrerPolicy="no-referrer"
            />
          </a>
          <div className="max-w-md text-center mb-8">
            <p className="text-xs font-light leading-relaxed text-neutral-400">
              Rare Handcrafted Luxury
            </p>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="https://www.instagram.com/kayoki.shop/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Kayoki on Instagram"
              className="hover:text-white transition-colors cursor-pointer"
            >
              <svg
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
            <a
              href="https://www.facebook.com/people/Kayoki/61579228918933"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Kayoki on Facebook"
              className="hover:text-white transition-colors cursor-pointer"
            >
              <svg
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
            <a
              href="https://wa.me/918920736634?text=Hi! I want to learn more about your fragrances"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contact on WhatsApp"
              className="hover:text-white transition-colors cursor-pointer"
            >
              <svg
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </a>
          </div>
        </div>

        <div className="max-w-[100rem] mx-auto flex flex-col md:flex-row justify-between items-center pt-8 text-[0.65rem] tracking-widest uppercase font-medium">
          <p>&copy; {new Date().getFullYear()} KAYOKI. All rights reserved.</p>
          <div className="flex gap-6 mt-4 md:mt-0 flex-wrap justify-center">
            <button
              onClick={() => {
                window.history.pushState({}, "", "/admin_access");
                setCurrentPath("/admin_access");
              }}
              className="hover:text-white transition-colors cursor-pointer hover:text-[#DFB37A]"
            >
              Admin
            </button>
            <button
              onClick={() => {
                window.history.pushState({}, "", "/privacy");
                setCurrentPath("/privacy");
              }}
              className="hover:text-[#DFB37A] transition-colors cursor-pointer"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => {
                window.history.pushState({}, "", "/terms");
                setCurrentPath("/terms");
              }}
              className="hover:text-[#DFB37A] transition-colors cursor-pointer"
            >
              Terms & Conditions
            </button>
            <button
              onClick={() => {
                window.history.pushState({}, "", "/refund");
                setCurrentPath("/refund");
              }}
              className="hover:text-[#DFB37A] transition-colors cursor-pointer"
            >
              Return & Refund
            </button>
          </div>
        </div>
      </footer>

      <OrderModal
        isOpen={isOrderModalOpen}
        onClose={() => setIsOrderModalOpen(false)}
        product={
          viewingPdpProduct || products?.find((p) => p.id === selectedProductId)
        }
      />
      </React.Suspense>
    </div>
  );
}
